import axios from 'axios';
import type { QaReview } from '../Componant/Dashboard/VideoCallQa/sampleReview';
import {
  WEB_API_BASEURL,
  API_URLS,
  BLAZE_BASEURL,
  DEMOOPS_HISTORY_BASEURL,
  VIDEOCALL_QA_BASEURL,
} from './api-constant';

import { write } from 'fs';

const isValidApiId = (id: unknown): boolean =>
    id !== undefined && id !== null && id !== '' && String(id) !== 'undefined';

// teacher profile api
export const getTeachers = async (page: number = 0, size: number = 100, searchPayload: { name?: string, emailId?: string, mobileNumber?: string, type?:any,resigned?:any } = {}) => {
    try {
        const response = await axios.post(API_URLS.getTeachers, { page, size, ...searchPayload });
        return response.data.content || [];
    } catch (error) {
        console.error("Error fetching teachers:", error);
        return [];
    }
};

/** Teachers assigned to the logged-in Teacher Leader. */
export const getTeacherLeaderTeachers = async (userId: string | number) => {
    try {
        const url = API_URLS.teacherLeaderTeachers.replace('{userId}', String(userId));
        const response = await axios.get(url);
        const data = response.data;
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.content)) return data.content;
        if (Array.isArray(data?.data)) return data.data;
        if (Array.isArray(data?.teachers)) return data.teachers;
        return [];
    } catch (error) {
        console.error("Error fetching teacher leader teachers:", error);
        return [];
    }
};

/** Assign teachers to a teacher leader: { userId, teacherIds }. */
export const assignTeachersToTeacherLeader = async (
    userId: string | number,
    teacherIds: Array<string | number>
) => {
    try {
        const response = await axios.put(API_URLS.assignTeacherLeaderTeachers, {
            userId: Number(userId),
            teacherIds: teacherIds
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id)),
        });
        return response;
    } catch (error) {
        console.error("Error assigning teachers to teacher leader:", error);
        throw error;
    }
};

/** Remove one teacher from a teacher leader: { userId, teacherId }. */
export const removeTeacherFromTeacherLeader = async (
    userId: string | number,
    teacherId: string | number
) => {
    try {
        const response = await axios.delete(API_URLS.assignTeacherLeaderTeachers, {
            data: {
                userId: Number(userId),
                teacherId: Number(teacherId),
            },
            headers: { "Content-Type": "application/json" },
        });
        return response;
    } catch (error) {
        console.error("Error removing teacher from teacher leader:", error);
        throw error;
    }
};

/** Promote a user to a role, e.g. TEACHER_LEADER. */
export const updateUserRole = async (credentialId: string | number, roleName: string) => {
    try {
        const response = await axios.put(API_URLS.updateUserRole, {
            credentialId: Number(credentialId),
            roleName,
        });
        return response;
    } catch (error) {
        console.error("Error updating user role:", error);
        throw error;
    }
};

const extractTeacherLeaderList = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.content)) return payload.content;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.leaders)) return payload.leaders;
    if (Array.isArray(payload?.teachers)) return payload.teachers;
    if (Array.isArray(payload?.result)) return payload.result;
    return [];
};

/** All teachers currently marked as teacher leaders. */
export const getTeacherLeaders = async () => {
    try {
        const response = await axios.get(API_URLS.teacherLeaderLeaders);
        return extractTeacherLeaderList(response.data);
    } catch (error) {
        console.error("Error fetching teacher leaders:", error);
        throw error;
    }
};

/** Create a teacher leader user: { email, firstName, lastName, countryCode, mobileNumber, role: TEACHER_LEADER }. */
export const createTeacherLeader = async (data: {
    email: string;
    firstName: string;
    lastName: string;
    countryCode: string;
    mobileNumber: string;
}) => {
    try {
        const response = await axios.post(API_URLS.createTeacherLeader, {
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            countryCode: data.countryCode,
            mobileNumber: data.mobileNumber,
            role: "TEACHER_LEADER",
        });
        return response;
    } catch (error) {
        console.error("Error creating teacher leader:", error);
        throw error;
    }
};
export const markTeacherLeader = async (userId: string | number, isTeacherLeader: boolean) => {
    try {
        const response = await axios.put(API_URLS.markTeacherLeader, {
            userId: Number(userId),
            isTeacherLeader,
        });
        return response;
    } catch (error) {
        console.error("Error marking teacher leader:", error);
        throw error;
    }
};

export const getActiveTeachers = async () => {
    try {
        const response = await axios.get(API_URLS.getActiveTeachers);
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Error fetching active teachers:', error);
        return [];
    }
};

export const addTeacher = async (data: any) => {
    try {
        const response = await axios.post(API_URLS. addTeacher, data);
        return response;
    } catch (error) {
        console.error('Error fetching adding teacher:', error);
        throw error;
    }
};
export const getOfflineTeachers = async (data: any) => {
    try {
        const response = await axios.post(API_URLS. getOfflineTeachers, data);
        return response;
    } catch (error) {
        console.error('Error fetching adding teacher:', error);
        throw error;
    }
};

export const getAddOfflineStudent = async (data :any) =>{
    try {
        const response = await axios.post(API_URLS. getAddOfflineStudent, data);
        return response;
    } catch (error) {
        console.error('Error fetching adding teacher:', error);
        throw error;
    }
}

// Offline payment (renewal payment link / record external payment) helpers.
// Branch/role resolution: returns the centre(s) assigned to the logged-in uId.
// Non-empty => offline-admin (restricted to those branches); empty => admin (all config branches).
export const getCounsellorCentre = async (uId: any) => {
    try {
        const url = API_URLS.counsellorCentre.replace('{uId}', uId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching counsellor centre:', error);
        throw error;
    }
}

export const getOfflineCustomerId = async (studentId: any, currency: any) => {
    try {
        const url = API_URLS.offlineCustomerId
            .replace('{studentId}', studentId)
            .replace('{currency}', currency);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching offline customer id:', error);
        return null;
    }
}

// Create razorpay payment link for an offline package (paymentFor: "directpayment").
export const createOfflineDirectPayment = async (data: any) => {
    const response = await axios.post(API_URLS.offlineDirectPayment, data, {
        headers: { "Content-type": "application/json; charset=UTF-8" }
    });
    return response;
}

// Record external (non-razorpay) offline payment and create classes directly (paymentFor: "nonrazorpay").
export const createOfflineExternalPayment = async (data: any) => {
    const response = await axios.post(API_URLS.offlineExternalPayment, data, {
        headers: { "Content-type": "application/json; charset=UTF-8" }
    });
    return response;
}

export const getCourses = async (
    data: any,
    options?: { notifySlack?: boolean },
) => {
    try {
        const url =
            options?.notifySlack === undefined
                ? API_URLS.getCourses
                : `${API_URLS.getCourses}?notifySlack=${options.notifySlack ? "true" : "false"}`;
        const response = await axios.post(url, data);
        return response;
    } catch (error) {
        console.error('Error fetching teacher', error);
        throw error;
    }
};

export type TeacherMappedCourse = {
    courseName?: string;
    teacherId?: string;
    level?: string;
    courseId?: string;
    teacherCourseLevelId?: number;
};

/** Returns mapped course levels for a teacher; empty when API returns {}, null, or no list. */
export const getTeacherMappedCourses = async (teacherId: string): Promise<TeacherMappedCourse[]> => {
    const response = await getCourses([
        {
            teacherId,
            status: 'getteachermapppingcourse',
        },
    ]);
    const responseData = response?.data;
    if (responseData == null || typeof responseData !== 'object') {
        return [];
    }
    const list = (responseData as { getcourseList?: unknown }).getcourseList;
    return Array.isArray(list) ? (list as TeacherMappedCourse[]) : [];
};

export type TeacherFreeSlotCalendarParams = {
    fromDate: string;
    consecutiveWeeks: number;
    courseIds?: number[];
    teacherIds?: number[];
    levels?: string[];
    ageGroupIds?: number[];
};

export const getTeacherFreeSlotCalendar = async (params: TeacherFreeSlotCalendarParams) => {
    try {
        const search = new URLSearchParams();
        search.set('fromDate', params.fromDate);
        search.set('consecutiveWeeks', String(params.consecutiveWeeks));
        params.courseIds?.forEach((id) => search.append('courseIds', String(id)));
        params.teacherIds?.forEach((id) => search.append('teacherIds', String(id)));
        params.levels?.forEach((level) => search.append('levels', level));
        params.ageGroupIds?.forEach((id) => search.append('ageGroupIds', String(id)));
        const url = `${API_URLS.teacherFreeSlotCalendar}?${search.toString()}`;
        const token = localStorage.getItem('token');
        const response = await axios.get(url, {
            headers: {
                Accept: 'application/json, text/plain, */*',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher free-slot calendar', error);
        throw error;
    }
};

export const addStudentConvo = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.addStudentConvo, data);
        return response;
    } catch (error) {
        console.error('Error fetching teacher', error);
        throw error;
    }
};
export const getConversationData = async ( studentUserId:any) => {
    try {
        let url = API_URLS.getConversationData;
        url = url
       .replace('{studentUserId}', studentUserId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
};
export type RmFeedbackRow = {
    class_id?: string | number;
    classNumber?: string | number;
    course_id?: string | number;
    createdAt?: string;
    updatedAt?: string;
    comments?: string;
    student_user_id?: string | number;
    lessonsRemaining?: string | number;
    teacherId?: string | number;
    teacherName?: string;
    courseName?: string;
    feedback_type?: string;
    expiry_date?: string;
    learningGoalsDiscussed?: boolean | string;
    renewalLikelihood?: string;
    classesRequiredToCompleteLevel?: string | number;
};

export const getRmFeedback = async (studentUserId: string | number) => {
    try {
        const response = await axios.get(API_URLS.rmFeedback, {
            params: { studentUserId },
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching RM feedback", error);
        throw error;
    }
};

export const getConversationSummary = async ( studentUserId:any) => {
    try {
        let url = API_URLS.getConversationSummary;
        url = url
       .replace('{studentUserId}', studentUserId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
};
export const changePolicy = async (data: any) => {
    try {
        const response = await axios.post(API_URLS. changePolicy, data);
        return response;
    } catch (error) {
        console.error('Error updating policy', error);
        throw error;
    }
};

export const changeLevel = async (data:any) => {
    try {
      const response = await axios.patch(API_URLS.changeLevel, data);
      return response;
    } catch (error) {
      console.error('Error changing level', error);
      throw error;
    }
  };

  export const markcompleteLevel = async (data:any) => {
    try {
      const response = await axios.patch(API_URLS.markcompleteLevel, data);
      return response;
    } catch (error) {
      console.error('Error marking level', error);
      throw error;
    }
  };

export const editTeacherProfile = async (data: any) => {
    try {
        const response = await axios.post(API_URLS. editTeacherProfile, data);
        return response;
    } catch (error) {
        console.error('Error fetching student for ae:', error);
        throw error;
    }
};

export const bulkDeleteAirlineSlots = async (payload: {
    teacherId: number;
    fromDate: string;
    toDate: string;
}) => {
    try {
        const response = await axios.post(API_URLS.schedulerBaseURL, payload);
        return response.data;
    } catch (error) {
        console.error('Error deleting airline slots:', error);
        throw error;
    }
};

export type BulkDeleteFreeSlotsScope = "AIRLINE" | "PAID";

export type BulkDeleteFreeSlotsTiming = {
    startTime: string;
    endTime: string;
};

export const bulkDeleteFreeSlots = async (payload: {
    teacherId: number;
    fromDate: string;
    toDate: string;
    scope: BulkDeleteFreeSlotsScope[];
    timings?: BulkDeleteFreeSlotsTiming[];
}) => {
    try {
        const response = await axios.post(API_URLS.schedulerBulkDeleteFreeSlots, payload);
        return response.data;
    } catch (error) {
        console.error('Error bulk-deleting free slots:', error);
        throw error;
    }
};

export const QaButton = async (data: any) => {
    try {
        // Create a new axios instance without default interceptors/auth
        const axiosNoAuth = axios.create({
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        const response = await axiosNoAuth.post(API_URLS. QaButton, data);
        return response;
    } catch (error) {
        console.error('Error calling QA API:', error);
        throw error;
    }
};

export const teacherProfile = async ( teacherId:any) => {
    try {
        let url = API_URLS.teacherProfile;
        url = url
       .replace('{teacherId}', teacherId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
};



export const teacherClassCount = async ( teacherId:any) => {
    try {
        let url = API_URLS.teacherClassCount;
        url = url
       .replace('{teacherId}', teacherId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
};

export type TeacherScheduleTemplateTiming = { id: number; startTime: string; endTime: string };
export type TeacherScheduleTemplateDay = { day: string; timings: TeacherScheduleTemplateTiming[] };
export type TeacherScheduleTemplatesResponse = {
    teacherId: number;
    effectiveFrom?: string;
    effectiveUntil?: string;
    isActive?: boolean;
    offlineCenterId?: number | null;
    days: TeacherScheduleTemplateDay[];
};

export const getTeacherScheduleTemplates = async (
    teacherId: string | number
): Promise<TeacherScheduleTemplatesResponse> => {
    try {
        let url = API_URLS.teacherScheduleTemplates;
        url = url.replace('{teacherId}', String(teacherId));
        const response = await axios.get<TeacherScheduleTemplatesResponse>(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher schedule templates', error);
        throw error;
    }
};

export type CreateTeacherScheduleTemplateTiming = { startTime: string; endTime: string };
export type CreateTeacherScheduleTemplateDay = { day: string; timings: CreateTeacherScheduleTemplateTiming[] };
export type CreateTeacherScheduleTemplateBody = {
    effectiveFrom: string;
    isActive?: boolean;
    offlineCenterId?: number;
    days: CreateTeacherScheduleTemplateDay[];
};

export const createTeacherScheduleTemplates = async (
    teacherId: string | number,
    body: CreateTeacherScheduleTemplateBody
): Promise<TeacherScheduleTemplatesResponse> => {
    try {
        let url = API_URLS.teacherScheduleTemplates;
        url = url.replace('{teacherId}', String(teacherId));
        const response = await axios.post<TeacherScheduleTemplatesResponse>(url, body);
        return response.data;
    } catch (error) {
        console.error('Error creating teacher schedule templates', error);
        throw error;
    }
};

/** PUT: always sends `effectiveFrom` (client uses current calendar date). */
export type UpdateTeacherScheduleTemplateBody = {
    effectiveFrom: string;
    isActive?: boolean;
    offlineCenterId?: number;
    /** When true, server applies the template right away; omit for next-cycle behavior. */
    applyImmediately?: boolean;
    days: CreateTeacherScheduleTemplateDay[];
};

export const updateTeacherScheduleTemplates = async (
    teacherId: string | number,
    body: UpdateTeacherScheduleTemplateBody
): Promise<TeacherScheduleTemplatesResponse> => {
    const effectiveFrom = body.effectiveFrom?.trim();
    if (!effectiveFrom) {
        throw new Error('updateTeacherScheduleTemplates: effectiveFrom is required on every PUT');
    }
    const payload: UpdateTeacherScheduleTemplateBody = { ...body, effectiveFrom };
    try {
        let url = API_URLS.teacherScheduleTemplates;
        url = url.replace('{teacherId}', String(teacherId));
        const response = await axios.put<TeacherScheduleTemplatesResponse>(url, payload);
        return response.data;
    } catch (error) {
        console.error('Error updating teacher schedule templates', error);
        throw error;
    }
};

export type DeleteTeacherScheduleTemplatesOptions = {
    /** When false, delete templates only and keep existing slots. */
    deleteSlots?: boolean;
    /** Prune slots from this date (YYYY-MM-DD) onward when deleting templates. */
    deleteFrom?: string;
};

export const deleteTeacherScheduleTemplates = async (
    teacherId: string | number,
    options?: DeleteTeacherScheduleTemplatesOptions
): Promise<void> => {
    try {
        let url = API_URLS.teacherScheduleTemplates;
        url = url.replace('{teacherId}', String(teacherId));
        const params = new URLSearchParams();
        if (options?.deleteSlots === false) {
            params.set('deleteSlots', 'false');
        }
        if (options?.deleteFrom?.trim()) {
            params.set('deleteFrom', options.deleteFrom.trim());
        }
        const query = params.toString();
        if (query) {
            url = `${url}?${query}`;
        }
        await axios.delete(url);
    } catch (error) {
        console.error('Error deleting teacher schedule templates', error);
        throw error;
    }
};

export type AeUnavailabilityTiming = { startTime: string; endTime: string };
export type AeUnavailabilitySlot = {
    date: string;
    isFullDay?: boolean;
    timings?: AeUnavailabilityTiming[];
};
export type CreateAeUnavailabilityBody = {
    reason?: string;
    slots: AeUnavailabilitySlot[];
};

export type AeUnavailabilityTimingResponse = {
    id: number;
    isFullDay: boolean;
    startTime: string;
    endTime: string;
    reason?: string | null;
};
export type AeUnavailabilitySlotResponse = {
    date: string;
    isFullDay: boolean;
    timings: AeUnavailabilityTimingResponse[] | null;
};
export type AeUnavailabilityResponse = {
    teacherId: number;
    totalRecords: number;
    slots: AeUnavailabilitySlotResponse[];
};

export const createAeUnavailability = async (
    teacherId: string | number,
    body: CreateAeUnavailabilityBody
) => {
    try {
        let url = API_URLS.aeUnavailability;
        url = url.replace('{teacherId}', String(teacherId));
        const response = await axios.post(url, body);
        return response.data;
    } catch (error) {
        console.error('Error creating AE unavailability', error);
        throw error;
    }
};

export const getAeUnavailability = async (
    teacherId: string | number,
    params?: { fromDate?: string; toDate?: string }
): Promise<AeUnavailabilityResponse> => {
    try {
        let url = API_URLS.aeUnavailability;
        url = url.replace('{teacherId}', String(teacherId));
        const query = new URLSearchParams();
        if (params?.fromDate) query.set('fromDate', params.fromDate);
        if (params?.toDate) query.set('toDate', params.toDate);
        const withQuery = query.toString() ? `${url}?${query.toString()}` : url;
        const response = await axios.get<AeUnavailabilityResponse>(withQuery);
        return response.data;
    } catch (error) {
        console.error('Error fetching AE unavailability', error);
        throw error;
    }
};

export const deleteAeUnavailabilityById = async (
    teacherId: string | number,
    unavailabilityId: string | number
): Promise<string> => {
    try {
        let url = API_URLS.aeUnavailability;
        url = url.replace('{teacherId}', String(teacherId));
        const response = await axios.delete(`${url}/${unavailabilityId}`);
        return typeof response.data === "string" ? response.data : "Deleted successfully";
    } catch (error) {
        console.error('Error deleting AE unavailability record', error);
        throw error;
    }
};

export const updateWorkingHours = async (updatedData: { teacherId: string; weekDay: string; hours: string }[]) => {
    try {
        const url = API_URLS.updateWorkingHours;
        
        console.log("Sending Data:", updatedData);

        const response = await axios.post(url, updatedData);
        return response.data;
    } catch (error) {
        console.error("Error updating working hours:", error);
        throw error;
    }
};


export const aeTeacherStudent = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.aeTeacherStudent, data);
        return response;
    } catch (error) {
        console.error('Error fetching student for ae:', error);
        throw error;
    }
};

export const teacherStudent = async (teacherUserId:any) => {
    try {
        let url = API_URLS.teacherStudent;
        url = url
       .replace('{teacherUserId}', teacherUserId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Students', error);
        throw error;
    }
};


export const ASSESSMENT_CRUD = async (assessmentType: any, payload: any) => {
    try {
        let url = API_URLS.ASSESSMENT_CRUD;

        if (url.includes("{assessmentType}")) {
            url = url.replace("{assessmentType}", encodeURIComponent(assessmentType.toString()));
        }

        console.log("Final API URL:", url); 

        // Ensure the payload is properly sent in the request body
        const response = await axios.post(url, payload, {
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response;
    } catch (error) {
        console.error("Error posting cash wallet data:", error);
        throw error;
    }
};
    

export const BEHAVIOURAL_ASSESSMENT = async (courseId:any) => {
    try {
        let url = API_URLS.BEHAVIOURAL_ASSESSMENT;
        url = url
       .replace('{courseId}', courseId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Students', error);
        throw error;
    }
};

export const classHistory = async ( studentId:any,courseId:any) => {
    try {
        let url = API_URLS.classHistory;
        url = url
       .replace('{studentId}', studentId)
       .replace('{courseId}', courseId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Students', error);
        throw error;
    }};
   
export const noShowDetails = async (
    teacherId: string,
    fromDate?: string,
    toDate?: string
  ) => {
    try {
      let url = API_URLS.noShowDetails.replace('{teacherId}', teacherId);
  
      if (fromDate && toDate) {
        // Replace date placeholders when both dates are provided
        url = url
          .replace('{fromDate}', fromDate)
          .replace('{toDate}', toDate);
      } else {
        // Remove date parameters if either date is missing
        url = url
          .replace('&fromDate={fromDate}', '')
          .replace('&toDate={toDate}', '');
      }
  
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching no-show details:', error);
      throw error;
    }
  };

export const noShowDelete = async (teacherId:any, teacherNoshowClassId:any) => {
    try {
        let url = API_URLS.noShowDelete;
        url = url
       .replace('{teacherId}', teacherId)
       .replace('(teacherNoshowClassId)', teacherNoshowClassId)

        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Students', error);
        throw error;
    }
};

export const cashWallet = async (userId: any, payload: any) => {
    try {
        let url = API_URLS.cashWallet;

        if (url.includes("{userId}")) {
            url = url.replace("{userId}", encodeURIComponent(userId.toString()));
        }

        console.log("Final API URL:", url); 

        // Ensure the payload is properly sent in the request body
        const response = await axios.post(url, payload, {
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response;
    } catch (error) {
        console.error("Error posting cash wallet data:", error);
        throw error;
    }
};


export const workingHours = async (teacherId:any) => {
    try {
        let url = API_URLS.workingHours;
        url = url
       .replace('{teacherId}', teacherId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Working hours', error);
        throw error;
    }
};

export const Profile = async (userId:any) => {
    try {
        let url = API_URLS.Profile;
        url = url
       .replace('{userId}', userId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Working hours', error);
        throw error;
    }
};

export const completedLessonByLevel = async (userId:any, levelId:any) => {
    try {
        let url = API_URLS.completedLessonByLevel;
        url = url
       .replace('{userId}', userId)
       .replace('{levelId}', levelId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Working hours', error);
        throw error;
    }
};



export const activeLevel = async (studentUserId:any, courseId:any) => {
    if (!isValidApiId(studentUserId)) {
        return { status: [] };
    }
    try {
        let url = API_URLS.activeLevel;
        url = url
       .replace('{studentUserId}', studentUserId)
       .replace('{courseId}',courseId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Working hours', error);
        throw error;
    }
};

export const completeLesson = async (studentUserId:any, classId:any) => {
    try {
        let url = API_URLS.completeLesson;
        url = url
       .replace('{studentUserId}', studentUserId)
       .replace('{classId}',classId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Working hours', error);
        throw error;
    }
};


export const ongoingLesson = async (userId:any, levelId:any) => {
    try {
        let url = API_URLS.ongoingLesson;
        url = url
       .replace('{userId}', userId)
       .replace('{levelId}',levelId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Working hours', error);
        throw error;
    }
};


export const classNotMarked = async (teacherUserId:any) => {
    try {
        let url = API_URLS.classNotMarked;
        url = url
       .replace('{teacherUserId}', teacherUserId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching classes', error);
        throw error;
    }
};


export const userProfile = async (userId:any, credentialId:any) => {
    try {
        let url = API_URLS.UserProfile;
        url = url
       .replace('{userId}', userId)
       .replace('{credentialId}', credentialId);       
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching classes', error);
        throw error;
    }
};

// student profile api


export const getStudents = async (page: number = 0, size: number = 100, searchPayload: { name?: string, emailId?: string, mobileNumber?: string , userType?:any ,offlineCenterId?: any, typeOfStudent?: any, studentId?: string, userId?: string, age?: string, gender?: string, region?: string } = {}) => {
    try {
        const response = await axios.post(API_URLS.getStudents, { page, size, ...searchPayload });
        return response.data.content || [];
    } catch (error) {
        console.error("Error fetching teachers:", error);
        return [];
    }
};




export const getWeeklySlotsOffline = async (courseId:any, centerId:any, teacherId?:any, studentId?:any, ageGroup?:any) =>{
    try {
        let url = API_URLS.weeklyslotoffline;
        url=url
        .replace('{courseId}', courseId)
        .replace('{centerId}', centerId);
        
        // Add query parameters for teacher, student, and age group filters
        const params = new URLSearchParams();
        if (teacherId && teacherId !== "all") {
            params.append('teacherId', teacherId);
        }
        if (studentId && studentId !== "all") {
            params.append('studentId', studentId);
        }
        if (ageGroup && ageGroup !== "all") {
            params.append('ageGroup', ageGroup);
        }
        
        if (params.toString()) {
            url += `&${params.toString()}`;
        }
        
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching weekly slots:", error);
        throw error;
    }
}
export const getTeachersOffline = async (centerId:any, courseId?:any) =>{
    try {
        let url = API_URLS.getTeachersOffline;
        url = url.replace('{centerId}', centerId);
        
        // Only add courseId if provided
        if (courseId) {
            url = url.replace('{courseId}', courseId);
        } else {
            // Remove courseId parameter if not provided
            url = url.replace('&courseId={courseId}', '').replace('courseId={courseId}&', '');
        }
        
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching teachers:", error);
        throw error;
    }
}
export const getStudentsOfflineNew = async (courseId:any, centerId:any, searchTerm: string = ""): Promise<any> =>{
    try {
        let url = API_URLS.getStudentsOffline;
        url=url
        .replace('{courseId}', courseId)
        .replace('{centerId}', centerId);
        
        // Add search parameter if provided
        if (searchTerm.trim()) {
            url += `&search=${encodeURIComponent(searchTerm.trim())}`;
        }
        
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching students:", error);
        throw error;
    }
}


export const getStudentsSlotDetails = async (studentId:any, fromDate:any, toDate:any) =>{

    try {
        let url = API_URLS.getStudentsSlotDetails;
        url = url
        .replace('{studentId}', studentId)
        .replace('{fromDate}', fromDate)
        .replace('{toDate}', toDate);
        const response = await axios.get(url);
        return response.data;
    }
    catch (error) {
        console.error("Error fetching students slot details:", error);
        throw error;
    }
}

 export const addStduentOffline = async (data:any) =>{
    try {
        let url = API_URLS.addStduentOffline;
        const response = await axios.post(url,data);
        return response.data;
    } catch (error) {
        console.error("Error fetching weekly slots:", error);
        throw error;
    }
}

export const removeStudentOffline = async (data: any) => {
    try {
        let url = API_URLS.removeStudentOffline;
        
        const response = await axios.delete(url, {
            data: data,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error removing student from offline slot', error);
        throw error;
    }
};

export const addCompensatoryOrRestrictedClass = async (slotId:any, data:any) => {
    try {
        const response = await axios.put(API_URLS.addCompensatoryOrRestrictedClass.replace('{slotId}', slotId), data);
        return response.data;
    } catch (error) {
        console.error('Error adding compensatory/restricted class:', error);
        throw error;
    }
};

export const sendSlackAlert = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.sendSlackAlert, data);
        return response.data;
    } catch (error) {
        console.error('Error sending Slack alert:', error);
        throw error;
    }
};

export const teacherSlotDetails = async (slotId:any) =>{
try {
    let url = API_URLS.teacherSlotDetails;
    url=url
    .replace('{slotId}', slotId)
    const response = await axios.get(url);
    return response.data;
} catch (error) {
    console.error("Error fetching weekly slots:", error);
    throw error;
}
}


export const getOfflineStudents = async (page: number = 0, size: number = 100, typeOfStudent = "offline", searchPayload: { name?: string, emailId?: string, mobileNumber?: string , userType?:any ,offlineCenterId?: any, typeOfStudent?: any ,} = {}) => {
    try {
        const response = await axios.post(API_URLS.getStudents, { page, size,typeOfStudent, ...searchPayload });
        return response.data.content || [];
    } catch (error) {
        console.error("Error fetching teachers:", error);
        return [];
    }
};

export const getOfflineStudentSlots = async (studentId: any, courseId: any, page: number = 0, size: number = 100) => {
    try {
        let url = API_URLS.getOfflineStudentSlots;
        url = url
            .replace('{studentId}', studentId)
            .replace('{courseId}', courseId)
            .replace('{page}', page.toString())
            .replace('{size}', size.toString());
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching offline student slots', error);
        throw error;
    }
};

export const getTeacherSlots = async (teacherUserId: any, fromDate: string, toDate: string, page: number = 0, size: number = 100) => {
    try {
        let url = API_URLS.getTeacherSlots;
        url = url
            .replace('{teacherUserId}', teacherUserId)
            .replace('{page}', page.toString())
            .replace('{size}', size.toString())
            .replace('{fromDate}', fromDate)
            .replace('{toDate}', toDate);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher slots', error);
        throw error;
    }
};

export const rescheduleOfflineSlot = async (existingStudentSlotId: number, newSlotId: number | null, newVirtualSlotId: string | null, remarks: string) => {
    try {
        const payload: any = {
            existingStudentSlotId,
            remarks
        };
        
        // Add newSlotId or newVirtualSlotId based on availability
        if (newSlotId) {
            payload.newSlotId = newSlotId;
        } else if (newVirtualSlotId) {
            payload.newVirtualSlotId = newVirtualSlotId;
        }
        
        const response = await axios.post(API_URLS.rescheduleOfflineSlot, payload);
        return response.data;
    } catch (error) {
        console.error('Error rescheduling offline slot', error);
        throw error;
    }
};

export const courseLevel = async (userId: any, courseId?: any) => {
    if (!isValidApiId(userId)) {
        return { status: [] };
    }
    try {
        let url = API_URLS.courseLevel;
        url = url.replace('{userId}', userId);
        
        // Add query parameters
        const params = new URLSearchParams();
        if (courseId) {
            params.append('courseId', courseId);
        }
        params.append('status', 'active');
        
        const response = await axios.get(`${url}?${params.toString()}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching course level', error);
        throw error;
    }
};

// export const stuHistory = async (studentId:any) => {
//     try {
//         let url = API_URLS.stuHistory;
//         url = url
//         .replace('{studentId}', studentId)
//         const response = await axios.get(url);
//         return response.data;
//     } catch (error) {
//         console.error('Error fetching student details', error);
//         throw error;
//     }
// };

export const studentProfile = async (credentialId: any, studentId:any) => {
    try {
        let url = API_URLS.StudentProfile;
        url = url
        .replace('{credentialId}', credentialId)
       .replace('{studentId}', studentId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
};

export const validateEmail = async (email:any) => {
    try {
        let url = API_URLS.validateEmail;
        url = url
       .replace('{email}', email);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error adding email', error);
        throw error;
    }
};

export const validateMobileNumber = async (mobileNumber:any) => {
    try {
        let url = API_URLS.validateMobileNumber;
        url = url
       .replace('{mobileNumber}', mobileNumber);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error adding mobile Number', error);
        throw error;
    }
};
export const updateLessonStatus = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.updateLessonStatus, data);
        return response;
    } catch (error) {
        console.error('Error updating lesson status:', error);
        throw error;
    }
};
export const addStudent = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.AddStudent, data);
        return response;
    } catch (error) {
        console.error('Error fetching student for ae:', error);
        throw error;
    }
};

export const addOfflineStudent = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.AddOfflineStudent, data);
        return response;
    } catch (error) {
        console.error('Error adding offline student', error);
        throw error;
    }
};

export const walletPoints = async (userId: any, userType:any) => {
    try {
        let url = API_URLS.walletPoints;
        url = url
        .replace('{userId}', userId)
       .replace('{userType}', userType);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching student wallet details', error);
        throw error;
    }
};

export const renewalPayment= async ( studentId:any) => {
    try {
        let url = API_URLS.renewalPayment;
        url = url
        .replace('{studentId}', studentId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching renewal details', error);
        throw error;
    }
};

export const getExternalPayment = async ( studentId:any) => {
    try {
        let url = API_URLS.getExternalPayment;
        url = url
        .replace('{studentId}', studentId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching transaction details', error);
        throw error;
    }
};

export const refundRequest = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.refundRequest, data);
        return response;
    } catch (error) {
        console.error('Error adding:', error);
        throw error;
    }
};

export const getRefundList = async ( studentId:any) => {
    try {
        let url = API_URLS.getRefundList;
        url = url
        .replace('{studentId}', studentId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching transaction details', error);
        throw error;
    }
};

export const getAllPayments = async ( studentId:any) => {
    try {
        let url = API_URLS.getAllPayments;
        url = url
        .replace('{studentId}', studentId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching transaction details', error);
        throw error;
    }
};

export const getInvoicePdf = async (invoiceId: any) => {
    try {
        const url = API_URLS.getInvoicePdf.replace('{invoiceId}', invoiceId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching invoice pdf', error);
        throw error;
    }
};

export const triggerRetryPayment = async ( payment:any) => {
    try {
        const response = await axios.post(API_URLS.triggerRetryPayment, payment);
        return response;
    } catch (error) {
        console.error('Error triggering retry payment', error);
        throw error;
    }
};

export const createCourseExtensionPaymentLink = async (payload: any) => {
    try {
        const response = await axios.post(API_URLS.courseExtensionPaymentLink, payload);
        return response.data;
    } catch (error) {
        console.error('Error creating course extension payment link', error);
        throw error;
    }
};

export const getCourseExtensionList = async (studentUserId: string, page: number = 1) => {
    try {
        const url = `${API_URLS.courseExtensionList.replace('{studentUserId}', studentUserId)}?page=${page}`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching course extension list', error);
        throw error;
    }
};

export const deleteCourseExtension = async (id: string, deletedBy: string) => {
    try {
        const response = await axios.put(API_URLS.courseExtensionDelete, { id, deletedBy });
        return response.data;
    } catch (error) {
        console.error('Error deleting course extension', error);
        throw error;
    }
};

export const getSubcriptionCourse = async ( studentId:any) => {
    try {
        let url = API_URLS.getSubcriptionCourse;
        url = url
        .replace('{studentId}', studentId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching transaction details', error);
        throw error;
    }
};
export const compensation= async (fromDate:any, toDate:any, emailId:any ) => {
    try {
        let url = API_URLS.compensation;
        url = url
       .replace('{fromDate}', fromDate)
       .replace('{toDate}', toDate)
       .replace('{emailId}', emailId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
};
export const leaveRequest = async (page: number, limit: number, search?: string) => {
    try {
      let url = API_URLS.leaveRequest;
      url = url.replace('{page}', page.toString()).replace('{limit}', limit.toString());
      
      // Add search parameter if provided
      if (search && search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
  
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching teacher leave requests:', error);
      throw error;
    }
  };

  export const getAffectedClassCount = async ( payload: any) => {
    try {
        let url = API_URLS.getAffectedClassCount;
   
        const response = await axios.post(url, payload, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        return response;
    } catch (error) {
        console.error("Error ", error);
        throw error;
    }
};

export const listOfAffectedCount = async ( payload: any) => {
    try {
        let url = API_URLS.listOfAffectedCount;
   
        const response = await axios.post(url, payload, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        return response;
    } catch (error) {
        console.error("Error ", error);
        throw error;
    }
};

  export const approveLeave = async (transactionId: any, transactionStatus: any) => {
    try {
        let url = API_URLS.approveLeave;
        
        // Replace placeholders with actual values
        url = url
            .replace('{transactionId}', encodeURIComponent(transactionId))
            .replace('{transactionStatus}', encodeURIComponent(transactionStatus));

        const response = await axios.post(url, {}, {  
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response.data;
    } catch (error) {
        console.error("Error approving leave:", error);
        throw error;
    }
};

export const uploadPayout = async (file: File, fromDate: string, toDate: string) => {
    try {
        let url = API_URLS.uploadPayout;
        url = url
            .replace('{fromDate}', encodeURIComponent(fromDate))
            .replace('{toDate}', encodeURIComponent(toDate));

        // Create a FormData object to send the file
        const formData = new FormData();
        formData.append('file', file); // Append the file to the FormData object

        // Make the POST request with the FormData
        const response = await axios.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data', // Set the content type for file upload
            },
        });

        return response.data; // Return the response data
    } catch (error) {
        console.error('Error uploading payout file:', error);
        throw error;
    }
};


export const resetotp = async (mobileNumber:any) => {
    try {
        let url = API_URLS.resetotp
            .replace('{mobileNumber}', encodeURIComponent(mobileNumber));
          

        const response = await axios.put(url, {
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response.data;
    } catch (error) {
        console.error("Error deleting payout:", error);
        throw error;
    }
};

export const deletePayout = async (fromDate: string, toDate: string) => {
    try {
        let url = API_URLS.deletePayout
            .replace('{fromDate}', encodeURIComponent(fromDate))
            .replace('{toDate}', encodeURIComponent(toDate));

        const response = await axios.put(url, {
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response.data;
    } catch (error) {
        console.error("Error deleting payout:", error);
        throw error;
    }
};
export const teacherChangeList = async () => {
    try {
        let url = API_URLS.TeacherChange;
        url = url
    
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
};

export const studentTeacherChange = async ( type:any ,teacherId: any) => {
    try {
        let url = API_URLS.studentTeacherChange;
        url = url
        .replace('{type}', type)
            .replace('{teacherId}', teacherId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
};

export const triggerTeacher = async (teacherId: any, payload?: { reasonDescription?: string }) => {
    try {
        let url = API_URLS.triggerTeacher;

        if (url.includes("{teacherId}")) {
            url = url.replace("{teacherId}", encodeURIComponent(teacherId.toString()));
        }
        const response = await axios.post(url, payload ?? {});
        return response;
    } catch (error) {
        
        console.error("Error deleting no-show:", error);
        throw error;
    }
};


export const completedToNotConducted = async (classId:any) => {
    try {
        let url = API_URLS.completedToNotConducted;

        if (url.includes("{classId}")) {
            url = url.replace("{classId}", encodeURIComponent(classId.toString()));
        }
        const response = await axios.post(url);
        return response;
    } catch (error) {
        console.error("Error deleting no-show:", error);
        throw error;
    }
};


export const scheduleToCompleted = async (classId:any) => {
    try {
        let url = API_URLS.scheduleToCompleted;

        if (url.includes("{classId}")) {
            url = url.replace("{classId}", encodeURIComponent(classId.toString()));
        }
        const response = await axios.post(url);
        return response;
    } catch (error) {
        console.error("Error deleting no-show:", error);
        throw error;
    }
};
export const completedToNoShow = async (classId: any, status: any) => {
    try {
      let url = API_URLS.completedToNoShow;
      url = url.replace("{classId}", encodeURIComponent(classId.toString()));
      url = url.replace("{status}", encodeURIComponent(status));
  
      // No body is sent, only URL with query/path params
      const response = await axios.post(url);
      return response;
    } catch (error) {
      console.error("Error deleting no-show:", error);
      throw error;
    }
  };
  
  
export const deleteTeacherSlot = async (slotId: any) => {
    try {
        let url = API_URLS.teacherSlotDelete;

        if (url.includes("{slotId}")) {
            url = url.replace(/{slotId}/g, encodeURIComponent(slotId.toString()));
        }

        const response = await axios.post(url);
        return response;
    } catch (error) {
        console.error("Error deleting teacher slot:", error);
        throw error;
    }
};


export const leaveDetails = async (teacherId:any) => {
    try {
        let url = API_URLS.LeaveDetails;
        url = url
       .replace('{teacherId}', teacherId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching leave Details', error);
        throw error;
    }}


export const addLeave = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.addLeave, data);
        return response;
    } catch (error) {
        console.error('Error addind leave:', error);
        throw error;
    }
};

export const addLeaveBalanceH1H2 = async (data: {
    teacherId: string | number;
    leaveYear?: string;
    h1balance: number;
    h1leaveRemaining: number;
    h1leaveAvailed: number;
    h2balance: number;
    h2leaveRemaining: number;
    h2leaveAvailed: number;
}) => {
    try {
        const response = await axios.post(API_URLS.addLeaveBalanceH1H2, data);
        return response;
    } catch (error) {
        console.error('Error updating leave balance H1/H2:', error);
        throw error;
    }
};

    export const leaveBalance = async (teacherId:any) => {
        try {
            let url = API_URLS.leaveBalance;
            url = url
           .replace('{teacherId}', teacherId);
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching leave Details', error);
            throw error;
        }}

        export const leaveBalanceV2 = async (teacherId:any) => {
            try {
                let url = API_URLS.leaveBalanceV2;
                url = url
               .replace('{teacherId}', teacherId);
                const response = await axios.get(url);
                return response.data;
            } catch (error) {
                console.error('Error fetching leave Details', error);
                throw error;
            }
        }

export const calculateNumberOfLeaves = async (data: {
    teacherId: number | string;
    fromDate: string;
    toDate: string;
    fromTime: string;
    toTime: string;
}) => {
    try {
        const response = await axios.post(API_URLS.calculateNumberOfLeaves, data);
        return response.data;
    } catch (error) {
        console.error('Error calculating number of leaves', error);
        throw error;
    }
};

export const applyTeacherLeave = async (data: {
    teacherId: number | string;
    fromDate: string;
    toDate: string;
    fromTime: string;
    toTime: string;
    reason: string;
    leaveDuration: number;
}) => {
    try {
        const response = await axios.post(API_URLS.applyTeacherLeave, data);
        return response;
    } catch (error) {
        console.error('Error applying teacher leave', error);
        throw error;
    }
};

export const getQaEntries = async (teacherId: any, page = 1, pageSize = 10) => {
    try {
        let url = API_URLS.getQaEntries;
        url = url.replace('{teacherId}', teacherId);
        const response = await axios.get(url, {
            params: { page, page_size: pageSize },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching AI QA entries', error);
        throw error;
    }
};

export const getReportsForAdmin = async (month: string) => {
    try {
        let url = API_URLS.getReportsForAdmin;
        url = url.replace('{month}', encodeURIComponent(month));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching admin teacher reports', error);
        throw error;
    }
};

export const getReportsForAdminRange = async (startMonth: string, endMonth: string) => {
    try {
        let url = API_URLS.getReportsForAdminRange;
        url = url
            .replace('{startMonth}', encodeURIComponent(startMonth))
            .replace('{endMonth}', encodeURIComponent(endMonth));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching admin teacher reports (range)', error);
        throw error;
    }
};

const extractTeacherReportRows = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.content)) return payload.content;
    if (Array.isArray(payload?.reports)) return payload.reports;
    if (Array.isArray(payload?.result)) return payload.result;
    return [];
};

/** Teacher Leader reports: only teachers assigned to that leader. */
export const getReportsForTeacherLeaderForAdmin = async (
    teacherLeaderUserId: string | number,
    range: { month: string } | { startMonth: string; endMonth: string },
    page: number = 1,
    limit: number = 100
) => {
    try {
        const allRows: any[] = [];
        let currentPage = page;
        const maxPages = 50;
        const isRange = "startMonth" in range;
        while (currentPage <= maxPages) {
            let url = isRange
                ? API_URLS.getReportsForTeacherLeaderForAdminRange
                : API_URLS.getReportsForTeacherLeaderForAdmin;
            url = url
                .replace('{teacherLeaderUserId}', encodeURIComponent(String(teacherLeaderUserId)))
                .replace('{page}', String(currentPage))
                .replace('{limit}', String(limit));
            if (isRange) {
                url = url
                    .replace('{startMonth}', encodeURIComponent(range.startMonth))
                    .replace('{endMonth}', encodeURIComponent(range.endMonth));
            } else {
                url = url.replace('{month}', encodeURIComponent(range.month));
            }
            const response = await axios.get(url);
            const payload = response.data;
            const rows = extractTeacherReportRows(payload);
            allRows.push(...rows);
            const totalPages =
                Number(payload?.total_pages ?? payload?.totalPages ?? payload?.pages) || 0;
            const hasNext = payload?.has_next ?? payload?.hasNext;
            const reachedLastPage = totalPages > 0 && currentPage >= totalPages;
            const noMore = hasNext === false || reachedLastPage || rows.length < limit;
            if (noMore) break;
            currentPage += 1;
        }
        return allRows;
    } catch (error) {
        console.error('Error fetching teacher leader reports', error);
        throw error;
    }
};

export const getTeacherMetricPastMonthsData = async (
    teacherId: string | number,
    parameter: string
) => {
    try {
        let url = API_URLS.getTeacherMetricPastMonthsData;
        url = url
            .replace('{teacherId}', encodeURIComponent(String(teacherId)))
            .replace('{parameter}', encodeURIComponent(parameter));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher metric past months data', error);
        throw error;
    }
};

        export const studentCourses = async (studentId:any) => {
            try {
                let url = API_URLS. studentCourses;
                url = url
               .replace('{studentId}', studentId);
                const response = await axios.get(url);
                return response.data;
            } catch (error) {
                console.error('Error fetching leave Details', error);
                throw error;
            }}

/** Graded exam list for a student course (scholar). */
export const getGradedExamList = async (courseId: string | number, userId: string | number) => {
    try {
        let url = API_URLS.gradedExamList;
        url = url
            .replace('{courseId}', String(courseId))
            .replace('{userId}', String(userId));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching graded exam list', error);
        throw error;
    }
};

/** Confirm external/manual graded exam payment. */
export const confirmGradedExamUserPayment = async (payload: Record<string, unknown>) => {
    try {
        const response = await axios.post(API_URLS.gradedExamUserPaymentConfirmation, payload, {
            headers: { "Content-Type": "application/json" },
        });
        return response.data;
    } catch (error) {
        console.error('Error confirming graded exam payment', error);
        throw error;
    }
};
/** Calendar POST may return [], null, {}, or nested arrays; callers expect a flat slot array. */
export function normalizeCalendarSlotsResponse(payload: unknown): any[] {
    if (payload == null) return [];
    if (Array.isArray(payload)) return payload;
    if (typeof payload !== "object") return [];
    const o = payload as Record<string, unknown>;
    const keys = ["data", "items", "content", "slots", "calendar", "records"] as const;
    for (const k of keys) {
        const v = o[k];
        if (Array.isArray(v)) return v;
    }
    return [];
}

            export const fetchCalendar = async (data: {
                fromDate: string;
                toDate: string;
                teacherUserId: string;
                actionBy: string;
            }) => {
                try {
                  const response = await axios.post(API_URLS.fetchCalendar, data, {
                    headers: {
                      "Content-Type": "application/json",
                    },
                  });

                  return normalizeCalendarSlotsResponse(response.data);
                } catch (error) {
                  throw error;
                }
              };


export const calendarData = async (studentUserId: any, courseId:any) => {
    try {
        let url = API_URLS.calendarData;
        url = url
        .replace('{studentUserId}', studentUserId)
       .replace('{courseId}', courseId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching calendar details', error);
        throw error;
    }
};

export const demoStudentClassCalendar = async (studentId: any, courseId: any, page: number = 0, size: number = 100) => {
    try {
        let url = API_URLS.demoStudentClassCalendar;
        url = url
            .replace(/{studentId}/g, String(studentId))
            .replace('{courseId}', String(courseId))
            .replace('{page}', String(page))
            .replace('{size}', String(size));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching demo student class calendar', error);
        throw error;
    }
};

export const reschedule = async (data: any) => {
    try {
        const response = await axios.post(API_URLS. reschedule, data);
        return response;
    } catch (error) {
        console.error('Error fetching student for ae:', error);
        throw error;
    }
};

export const insertSlot = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.insertSlot, data);
        return response;
    } catch (error) {
        console.error('Error inserting slot:', error);
        throw error;
    }
};

export const schedule = async (data: any) => {
    try {
        const response = await axios.post(API_URLS. schedule, data);
        return response;
    } catch (error) {
        console.error('Error fetching student for ae:', error);
        throw error;
    }
};

export const studentPaidClassSchedule = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.studentPaidClassSchedule, data);
        return response;
    } catch (error) {
        console.error('Error scheduling paid classes:', error);
        throw error;
    }
};

export const getMyClassesData = async (studentUserId: string | number, courseId: string | number) => {
    try {
        const url = API_URLS.myClassesData
            .replace('{studentUserId}', String(studentUserId))
            .replace('{courseId}', String(courseId));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching my classes data:', error);
        throw error;
    }
};

export const listDemoFreeSlots = async (
    userId: any,
    options?: { enablePaidTeacher?: boolean }
) => {
    try {
        const baseUrl = API_URLS.listDemoFreeSlots.replace(/{userId}/g, String(userId));
        const url = options?.enablePaidTeacher
            ? baseUrl.replace("customParameter=adminportal", "enablePaidTeacher=true")
            : baseUrl;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching demo free slots:', error);
        throw error;
    }
};

/** Tag the student with the interest (course tag) picked for a demo; listFreeSlots offers slots for this tag. */
export const updateStudentInterestTag = async (studentId: string | number, tagId: string | number) => {
    try {
        const url = API_URLS.updateStudentInterestTag
            .replace('{studentId}', String(studentId))
            .replace('{tagId}', String(tagId));
        const response = await axios.post(url);
        return response.data;
    } catch (error) {
        console.error('Error updating student interest tag:', error);
        throw error;
    }
};

export const allocateTeacherForBooking = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.allocateTeacherForBooking, data);
        return response.data;
    } catch (error) {
        console.error('Error allocating teacher for booking:', error);
        throw error;
    }
};

/** Fire student Mixpanel events (book/reschedule) via the referral trigger API. */
export const triggerMixpanelEvent = async (
    eventName: string,
    data: Record<string, unknown>
) => {
    try {
        const response = await axios.post(API_URLS.triggerMixpanel, { eventName, data });
        return response.data;
    } catch (error) {
        console.error("Error triggering Mixpanel event:", error);
        throw error;
    }
};

/** Persist UTM / campaign attribution for a student user before demo book. */
export const updateUserCampaign = async (
    studentUserId: string | number,
    data: {
        utmSource?: string | null;
        utmCampaign?: string | null;
        utmMedium?: string | null;
        gclid?: string | null;
        utmContent?: string | null;
        utmTerm?: string | null;
        audience?: string | null;
        market?: string | null;
        placement?: string | null;
        referringHostName?: string | null;
        referringUrl?: string | null;
    }
) => {
    try {
        const url = API_URLS.userCampaign.replace('{userId}', String(studentUserId));
        const response = await axios.post(url, data);
        return response.data;
    } catch (error) {
        console.error('Error updating user campaign:', error);
        throw error;
    }
};

export const airlineTeacherList = async (classScheduleId: any) => {
    try {
        let url = API_URLS.airlineTeacherList;
        url = url.replace(/{classScheduleId}/g, String(classScheduleId));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching airline teacher list:', error);
        throw error;
    }
};

export const reassignAirlineTeacher = async (classScheduleId: any, airlineTeacherSlot: any) => {
    try {
        let url = API_URLS.reassignAirlineTeacher;
        url = url
            .replace(/{classScheduleId}/g, String(classScheduleId))
            .replace(/{airlineTeacherSlot}/g, String(airlineTeacherSlot));
        const response = await axios.post(url, {
            classScheduleId: Number(classScheduleId),
            airlineTeacherSlot: Number(airlineTeacherSlot),
        });
        return response.data;
    } catch (error) {
        console.error('Error reassigning airline teacher:', error);
        throw error;
    }
};

export const completeDemoClass = async (csid: any, tid: any) => {
    try {
        let url = API_URLS.completeDemoClass;
        url = url
            .replace(/{st}/g, "class_completed")
            .replace(/{csid}/g, String(csid))
            .replace(/{tid}/g, String(tid));
        const response = await axios.post(url, {
            csid: String(csid),
            tid: String(tid),
            st: "class_completed",
        });
        return response.data;
    } catch (error) {
        console.error('Error completing demo class:', error);
        throw error;
    }
};

export const cancelDemoClass = async (
    csid: any,
    tid: any,
    cancelst: string,
    cancellationReasonByStudent: string,
) => {
    try {
        const url = `${API_URLS.completeDemoClass}`
            .replace(/{st}/g, "class_cancelled")
            .replace(/{csid}/g, String(csid))
            .replace(/{tid}/g, String(tid))
            .replace("cancelst=undefined", `cancelst=${encodeURIComponent(cancelst || "")}`)
            .replace(
                "cancellationReasonByStudent=undefined",
                `cancellationReasonByStudent=${encodeURIComponent(cancellationReasonByStudent || "")}`,
            );
        const response = await axios.post(url, {
            csid: String(csid),
            tid: String(tid),
            st: "class_cancelled",
            cancellationReasonByStudent,
            cancelst,
        });
        return response.data;
    } catch (error) {
        console.error('Error cancelling demo class:', error);
        throw error;
    }
};

export const fetchClassesCancelledByTeacher = async (studentId: any) => {
    try {
        let url = API_URLS.fetchClassesCancelledByTeacher;
        url = url.replace(/{studentId}/g, String(studentId));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching cancellation reasons by teacher:', error);
        throw error;
    }
};

export const studentClassSchedule = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.studentClassSchedule, data);
        return response.data;
    } catch (error) {
        console.error('Error scheduling student classes:', error);
        throw error;
    }
};

export const updateOnboardingStatus = async (enrollmentId: string | number, status: string) => {
    try {
        let url = API_URLS.updateOnboardingStatus;
        url = url
            .replace('{enrollmentId}', String(enrollmentId))
            .replace('{status}', String(status));
        const response = await axios.post(url);
        return response.data;
    } catch (error) {
        console.error('Error updating onboarding status:', error);
        throw error;
    }
};

export const assignOnboardingTeacher = async (
    enrollmentId: string | number,
    teacherId: string | number
) => {
    try {
        let url = API_URLS.assignOnboardingTeacher;
        url = url
            .replace('{enrollmentId}', String(enrollmentId))
            .replace('{teacherId}', String(teacherId));
        const response = await axios.post(url);
        return response.data;
    } catch (error) {
        console.error('Error assigning onboarding teacher:', error);
        throw error;
    }
};

export const postTeacherAssignmentAlert = async (
    enrollmentId: string | number,
    teacherId: string | number
) => {
    try {
        let url = API_URLS.teacherAssignmentAlert;
        url = url
            .replace('{enrollmentId}', String(enrollmentId))
            .replace('{teacherId}', String(teacherId));
        const response = await axios.post(url);
        return response.data;
    } catch (error) {
        console.error('Error posting teacher assignment alert:', error);
        throw error;
    }
};
export const completedClass = async (data: any) => {
    try {
        const response = await axios.post(API_URLS. completedClass, data);
        return response;
    } catch (error) {
        console.error('Error fetching student for ae:', error);
        throw error;
    }
};

export const classRecording = async ( teacherCredentialId:any, classId:any) => {
    try {
        let url = API_URLS.classRecording;
        url = url
       .replace('{teacherCredentialId}', teacherCredentialId)
       .replace('{classId}', classId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
};
// export const cancelClass = async (data: any) => {
//     try {
//         const response = await axios.post(API_URLS. cancelClass, data);
//         return response;
//     } catch (error) {
//         console.error('Error fetching student for ae:', error);
//         throw error;
//     }
// };

export const cancelTheClass = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.cancelTheClass, data);
        return response;
    } catch (error) {
        console.error('Error fetching student for ae:', error);
        throw error;
    }
};

export const UpdateProfile = async (data: any) => {
    try {
        let url = API_URLS.UpdateProfile;
        const response = await axios.put(url, data);
        return response.data;
    } catch (error) {
        console.error('Error updating profile:', error);
        throw error;
    }
};

export const reactivateStudent = async (studentId: string | number) => {
    try {
        const url = API_URLS.studentReactivate.replace(
            "{studentId}",
            encodeURIComponent(String(studentId))
        );
        const response = await axios.put(url);
        return response.data;
    } catch (error) {
        console.error("Error updating student active status:", error);
        throw error;
    }
};

export const membderShip = async (studentId: any, courseId: any, membershipType:any) => {
    try {
        let url = API_URLS.membderShip
            .replace('{studentId}', encodeURIComponent(studentId))
            .replace('{courseId}', encodeURIComponent(courseId))
            .replace('{membershipType}', encodeURIComponent(membershipType));
            

        const response = await axios.put(url, {
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response.data;
    } catch (error) {
        console.error("Error deleting payout:", error);
        throw error;
    }
};


export const getMemberShip = async (studentId:any, courseId:any) => {
    try {
        let url = API_URLS.getMemberShip;
        url = url
       .replace('{studentId}', studentId)
       .replace('{courseId}', courseId);

        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching membership', error);
        throw error;
    }
};

export const loginOtp = async (credentialId:any) => {
    try {
        let url = API_URLS.loginOtp;

        if (url.includes("{credentialId}")) {
            url = url.replace("{credentialId}", encodeURIComponent(credentialId.toString()));
        }
        const response = await axios.post(url);
        return response;
    } catch (error) {
        console.error("Error getting Otp", error);
        throw error;
    }
};

export const syncSalesForce = async (studentId: any) => {
    try {
        let url = API_URLS.syncSalesForce;

        // Debugging: Log the initial URL
        console.log("Initial URL:", url);

        if (url.includes("{studentId}")) {
            url = url.replace("{studentId}", encodeURIComponent(studentId.toString()));
        }

        // Debugging: Log the final URL
        console.log("Final URL:", url);

        const response = await axios.post(url);

        // Debugging: Log the API response
        console.log("API Response:", response.data);

        return response;
    } catch (error) {
       
      

        throw error;
    }
};

export const changeStatus = async (
  mobileNumber: string,
  status: string,
  lastDate?: string,
  reason?: string,
  lastDateBody?: string
) => {
    try {
      let url = API_URLS.changeStatus;
  
      // Replace placeholders with actual values
      url = url
        .replace('{mobileNumber}', encodeURIComponent(mobileNumber))
        .replace('{status}', encodeURIComponent(status));
  
      // Remove lastDate placeholder if not provided
      if (!lastDate) {
        url = url.replace('&lastDate={lastDate}', '');
      } else {
        url = url.replace('{lastDate}', encodeURIComponent(lastDate));
      }

      if (!reason || !String(reason).trim()) {
        url = url.replace('&reason={reason}', '');
      } else {
        url = url.replace('{reason}', encodeURIComponent(String(reason).trim().slice(0, 300)));
      }
  
      // Prepare the payload
      const payload: any = {};
  
      // Query uses "YYYY-MM-DD HH:mm:ss". Body uses "YYYY-MM-DD+HH:mm:ss".
      // Last working day is required for resign and terminate.
      if ((status === "RESIGNED" || status === "TERMINATED") && (lastDateBody || lastDate)) {
        payload.lastDate = lastDateBody || lastDate;
      }
  
      const response = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
  
      return response.data;
    } catch (error) {
      console.error("Error changing status:", error);
      throw error;
    }
  };
  export const enableForNightSlots = async (teacherId: any, enabled: any) => {
    try {
      const url = API_URLS.enableForNightSlots
        .replace('{teacherId}', encodeURIComponent(teacherId))
        .replace('{enabled}', encodeURIComponent(enabled));
  
      const response = await axios.put(url, null, {
        headers: {
          "Content-Type": "application/json",
        },
      });
  
      return response.data;
    } catch (error) {
      console.error("Error enabling night slots:", error);
      throw error;
    }
  };
  
  export const enableForPaid = async (teacherId: any, action: any) => {
    try {
      let url = API_URLS.enableForPaid;
      if (url.includes("{teacherId}")) {
        url = url.replace("{teacherId}", teacherId.toString());
      }
      if (url.includes("{action}")) {
        url = url.replace("{action}", action.toString());
      }
      if (!url.includes("teacherId=") && !url.includes("action=")) {
        url += `?teacherId=${teacherId}&action=${action}`;
      }
      const response = await axios.post(url, {}, {
        headers: {
          "Content-Type": "application/json",
        },
      });
  
      return response.data;
    } catch (error) {
      console.error("Error updating enable for paid status:", error);
      throw error;
    }
  };

  export const enableForAllocationPriority = async (param: any) => {
    try {
      let url = API_URLS.enableForAllocationPriority;
      
      const response = await axios.post(url, param, {
        headers: {
          "Content-Type": "application/json",
        },
      });
  
      return response;
    } catch (error) {
      console.error("Error updating enable for paid status:", error);
      throw error;
    }
  };

  export const FindTeacherStudent = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.FindTeacherStudent, data);
        return response;
    } catch (error) {
        console.error('Error fetching teacher student:', error);
        throw error;
    }
};

export const FindTeacherStudentV2 = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.FindTeacherStudentV2, data);
        return response;
    } catch (error) {
        console.error('Error fetching teacher student:', error);
        throw error;
    }
};

export const previewSchedule = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.previewSchedule, data);
        return response.data;
    } catch (error) {
        console.error('Error fetching schedule preview:', error);
        throw error;
    }
};

export type OnboardingTimeBucket = {
  id: string;
  dayPart?: string;
  dayPartLabel?: string;
  label?: string;
  status?: string;
};

export type OnboardingTeachersV3Response = {
  teachers?: any[];
};

/** Day-part buckets (morning / afternoon / evening / night) for preferred days. */
export const getOnboardingTimeBucketsV3 = async (body: {
  enrollmentId: string | number;
  selectedDays: string[];
  startDate: string;
}): Promise<{ buckets?: OnboardingTimeBucket[]; matchType?: string; matchedRung?: { days?: string[] } }> => {
  const { data } = await axios.post(API_URLS.onboardingTimeBucketsV3, body, {
    headers: { "Content-Type": "application/json" },
  });
  return data ?? {};
};

/** Ranked teachers available in the chosen day-part bucket. */
export const getOnboardingTeachersV3 = async (body: {
  enrollmentId: string | number;
  bucket: string;
  selectedDays: string[];
  startDate: string;
}): Promise<OnboardingTeachersV3Response> => {
  const { data } = await axios.post(API_URLS.onboardingTeachersV3, body, {
    headers: { "Content-Type": "application/json" },
  });
  return data ?? {};
};

/** Recurring schedule preview for exact day/time selections (onboarding v3). */
export const getOnboardingSchedulePreviewV3 = async (body: {
  enrollmentId: string | number;
  teacherUserId: string | number;
  startDate: string;
  selections: { day: string; time: string }[];
}) => {
  const { data } = await axios.post(API_URLS.onboardingSchedulePreviewV3, body, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
};


export const generateReferral = async (data: any) => {
    try {
      const response = await axios.post(API_URLS.referralLink, data, {
        headers: {
          "Content-Type": "application/json",
     
        },
      });
      console.log("API Response:", response.data);
  
      return response.data; 
    } catch (error) {
 
      throw error; 
    }
  };

  export const AcademicExpertCalendar = async ({ teacherId, fromDate, toDate, parentTagId }: 
    { teacherId: any, fromDate: any, toDate: any, parentTagId: any }) => {
    try {
        let url = API_URLS.AeCalendar;
        url = url
            .replace('{teacherId}', teacherId)
            .replace('{fromDate}', fromDate)
            .replace('{toDate}', toDate)
            .replace('{parentTagId}', parentTagId);
            
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Students', error);
        throw error;
    }
};

export const deleteAe = async (airlineTeacherSlotid: any) => {
    try {
        let url = API_URLS.deleteAe;
        url = url.replace('{airlineTeacherSlotid}', airlineTeacherSlotid);
        const response = await axios.delete(url);
        return response.data;
    } catch (error) {
        console.error('Error deleting AE', error);
        throw error;
    }
};

export const addAeSlot = async ({
    teacherId,
    clientId,
    ishalfHourSlot,
    maxNumberOfSlots,
    categoryId,
    slots,
}: {
    teacherId: any;
    clientId: number;
    ishalfHourSlot: boolean;
    maxNumberOfSlots: number;
    categoryId: number;
    slots: Array<{ isInternationalSlot: boolean; slotDate: string; startTime: string; endTime: string }>;
}) => {
    try {
        let url = API_URLS.addAeSlot;
        url = url
            .replace('{teacherId}', teacherId)
            .replace('{clientId}', String(clientId))
            .replace('{ishalfHourSlot}', String(ishalfHourSlot))
            .replace('{maxNumberOfSlots}', String(maxNumberOfSlots))
            .replace('{categoryId}', String(categoryId));
        const response = await axios.post(url, slots);
        return response.data;
    } catch (error) {
        console.error('Error adding AE slot', error);
        throw error;
    }
};

export const getAeAirlineBookings = async (slotDate: string, parentTagId: string) => {
    try {
        let url = API_URLS.aeAirlineBookings;
        url = url.replace('{slotDate}', slotDate).replace('{parentTagId}', parentTagId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching airline bookings', error);
        throw error;
    }
};

/** Airline slot utilization by date (international vs domestic, by category). */
export const getAeAirlineDetails = async (fromDate: string, toDate: string) => {
    try {
        let url = API_URLS.aeAirlineDetails;
        url = url.replace('{fromDate}', fromDate).replace('{toDate}', toDate);
        const raw = localStorage.getItem('authToken') || localStorage.getItem('token');
        const authorization = raw
            ? raw.startsWith('Bearer ')
                ? raw
                : `Bearer ${raw}`
            : undefined;
        const response = await axios.get(url, {
            headers: {
                Accept: 'application/json, text/plain, */*',
                ...(authorization ? { Authorization: authorization } : {}),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching airline details', error);
        throw error;
    }
};

/** Category × teacher AE slot matrix for one calendar date. */
export const getAeCategoryWiseSlots = async (date: string) => {
    try {
        let url = API_URLS.aeCategoryWiseSlots;
        url = url.replace('{date}', date);
        const raw = localStorage.getItem('authToken') || localStorage.getItem('token');
        const authorization = raw
            ? raw.startsWith('Bearer ')
                ? raw
                : `Bearer ${raw}`
            : undefined;
        const response = await axios.get(url, {
            headers: {
                Accept: 'application/json, text/plain, */*',
                ...(authorization ? { Authorization: authorization } : {}),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching AE category-wise slots', error);
        throw error;
    }
};

export const getAeConversionReport = async (fromDate: string, toDate: string) => {
    try {
        let url = API_URLS.aeConversion;
        url = url.replace('{fromDate}', fromDate).replace('{toDate}', toDate);
        const raw = localStorage.getItem('authToken') || localStorage.getItem('token');
        const authorization = raw
            ? raw.startsWith('Bearer ')
                ? raw
                : `Bearer ${raw}`
            : undefined;
        const response = await axios.get(url, {
            headers: {
                Accept: 'application/json, text/plain, */*',
                ...(authorization ? { Authorization: authorization } : {}),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching AE conversion report', error);
        throw error;
    }
};

export const qaList= async (teacherId:any) => {
    try {
        let url = API_URLS.qaList;
        url = url
       .replace('{teacherId}', teacherId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching classes', error);
        throw error;
    }
};


export const getPackageList = async (studentId:any) => {
    try {
        let url = API_URLS.getPackageList;
        url = url.replace('{studentId}', studentId);
                const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching payment link", error);
        throw error;
    }
};



export const filteredTeacherByCourseId = async ( payload: any) => {
    try {
        let url = API_URLS.filteredTeacherByCourseId;


        console.log("Final API URL:", url); 

   
        const response = await axios.post(url, payload, {
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response;
    } catch (error) {
        console.error("Error ", error);
        throw error;
    }
};

export const newFetchTeacherByCourseId = async ( payload: any) => {
    try {
        let url = API_URLS.newFetchTeacherByCourseId;
   
        const response = await axios.post(url, payload, {
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response;
    } catch (error) {
        console.error("Error ", error);
        throw error;
    }
};

export const getArtiumCash = async (userId:any, userType:any) => {
    
    try {
        let url = API_URLS.getArtiumCash.replace('{userId}', userId).replace('{userType}', userType.toLowerCase());
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
  }

export type UtilityTimezone = {
    id: string | number;
    name?: string;
    type: string;
    timezone?: string;
};

export const getUtilityTimezones = async (): Promise<{ timezones?: UtilityTimezone[] }> => {
    try {
        const axiosNoAuth = axios.create({
            headers: {
                'Content-Type': 'application/json',
            },
        });
        const response = await axiosNoAuth.get(API_URLS.utilityTimezones);
        return response.data;
    } catch (error) {
        console.error("Error fetching utility timezones:", error);
        throw error;
    }
};

export const getStudentDemoCompleteStatus = async (studentId: any) => {
    if (!isValidApiId(studentId)) {
        return null;
    }
    try {
        const url = API_URLS.studentDemoComplete.replace('{studentId}', String(studentId));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching student demo complete status:', error);
        throw error;
    }
};

  export const getCoursePrice = async (levelIds:any, countryCode:any, countryName:any) => {
    try {
        let url = API_URLS.getCoursePrice.replace('{levelIds}', encodeURIComponent(JSON.stringify(levelIds))).replace('{countryCode}', encodeURIComponent(countryCode)).replace('{countryName}', encodeURIComponent(countryName));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
  }

  // Offline course price. Returns { data: [{ levelId, price, centerPriceMap: { [centerId]: price }, ... }] }.
  // The caller resolves the effective price for a level via centerPriceMap[centerId] (falling back to price).
  export const getMultipleOfflineCoursePrice = async (levelIds:any, countryCode:any, centerId:any) => {
    try {
        let url = API_URLS.getMultipleOfflineCoursePrice
            .replace('{levelIds}', encodeURIComponent(JSON.stringify(levelIds)))
            .replace('{countryCode}', encodeURIComponent(countryCode))
            .replace('{centerId}', encodeURIComponent(centerId));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching offline course price', error);
        throw error;
    }
  }

  export const getAccountDetails = async (levelId:any) => {
    try {
        let url = API_URLS.getAccountDetails.replace('{levelId}', levelId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
  }

  export const savePackage = async (data: any) => { 
    try {
        const response = await axios.post(API_URLS.savePackage, data);
        return response;
    } catch (error) {
        console.error('Error fetching student for ae:', error);
        throw error;
    }
  }

export const triggerExternalPayment = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.externalPayment, data);
        return response;
    } catch (error) {
        console.error('Error fetching student for ae:', error);
        throw error;
    }
};
   

export const createPackage = async (studentId: any, courseId: any, teacherId: any, teacherName: any) => {
    try {
        let url = API_URLS.createPackage;
        if (url.includes("{studentId}")) {
            url = url.replace("{studentId}", encodeURIComponent(studentId.toString()));
        }
        if (url.includes("{courseId}")) {
            url = url.replace("{courseId}", encodeURIComponent(courseId.toString()));
        }
        if(teacherId){
            url = url + `&teacherId=${teacherId}&teacherName=${teacherName || ""}`;
        }
        
        console.log("Final API URL:", url);
        const response = await axios.get(url, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        return response;
    } catch (error) {
        console.error("Error ", error);
        throw error;
    }
};

export const aeForm = async ( studentId:any) => {
    try {
        let url = API_URLS.aeForm;
        url = url
       .replace('{studentId}', studentId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
};

export const kysForm = async (courseId: any, userId: any) => {
    try {
        let url = API_URLS.kysForm;
        url = url
        .replace('{courseId}', courseId)
        .replace('{userId}', userId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching kys details', error);
        throw error;
    }
};

export const approveRequestRefund = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.approveRequestRefund, data);
        return response;
    } catch (error) {
        console.error('Error approving:', error);
        throw error;
    }
};

export const rejectRequestRefund = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.rejectRequestRefund, data);
        return response;
    } catch (error) {
        console.error('Error reject refund:', error);
        throw error;
    }
};

export const getRefundData = async () => {
    try {
        let url = API_URLS.getRefundData;
        url = url
    
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching refund details', error);
        throw error;
    }
};


export const expireyHistory = async (studentId: any, courseId:any) => {
    try {
        let url = API_URLS.expireyHistory;
        url = url
        .replace('{studentId}', studentId)
       .replace('{courseId}', courseId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching calendar details', error);
        throw error;
    }
};

export const changeTeacherHistory = async (studentId: any,) => {
    try {
        let url = API_URLS.changeTeacherHistory;
        url = url
        .replace('{studentId}', studentId)
  
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching calendar details', error);
        throw error;
    }
};

export const deductHistory = async (studentId: any) => {
    try {
        let url = API_URLS.deductHistory;
        url = url
        .replace('{studentId}', studentId)
  
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching calendar details', error);
        throw error;
    }
};

export const getQaList = async () => {
    try {
        let url = API_URLS.getQaList;
        url = url
    
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching qa List', error);
        throw error;
    }
};

export const updateQaList = async (updatedData: any) => {  // Accepts updated data
    try {
        let url = API_URLS.updateQaList;

        const response = await axios.put(url, updatedData, {  
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response.data;
    } catch (error) {
        console.error('Error updating QA List', error);
        throw error;
    }
};

export const freeAttempt = async (updatedData: any) => {  // Accepts updated data
    try {
        let url = API_URLS.freeAttempt;

        const response = await axios.put(url, updatedData, {  
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response.data;
    } catch (error) {
        console.error('Error updating QA List', error);
        throw error;
    }
};

export const freeAttemptCount = async (studentId:any, courseId:any) => {
    try {
        let url = API_URLS.freeAttemptCount;
        url = url
       .replace('{studentId}', studentId)
       .replace('{courseId}', courseId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Students', error);
        throw error;
    }
};

export const getGraded = async (studentId:any, courseId:any, status:any) => {
    try {
        let url = API_URLS.getGraded;
        url = url
       .replace('{studentId}', studentId)
       .replace('{courseId}', courseId)
       .replace('{status}', status);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Students', error);
        throw error;
    }
};


// export const qaReports = async () => {
//     try {
//         const url = API_URLS.qaReports;
//         const response = await axios.get(url);
//         return response.data;
//     } catch (error) {
//         console.error("Error fetching QA List", error);
//         throw error;
//     }
// };


export const addNewQaList = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.addNewQaList, data);
        return response;
    } catch (error) {
        console.error('Error adding:', error);
        throw error;
    }
};


export const SocialTrigger= async (data: any) => {
    try {
        const response = await axios.post(API_URLS.SocialTrigger, data);
        return response;
    } catch (error) {
        console.error('Error adding:', error);
        throw error;
    }
};

export const extendExpriey= async (data: any) => {
    try {
        const response = await axios.post(API_URLS.extendExpriey, data);
        return response;
    } catch (error) {
        console.error('Error adding:', error);
        throw error;
    }
};

export const extendOfflineEnrollmentExpiry = async (
    enrollmentId: string | number,
    data: { newExpiryDate: string; reason: string }
) => {
    try {
        const url = API_URLS.extendOfflineEnrollmentExpiry.replace(
            '{enrollmentId}',
            String(enrollmentId)
        );
        const response = await axios.post(url, data);
        return response.data;
    } catch (error) {
        console.error('Error extending offline enrollment expiry:', error);
        throw error;
    }
};

export const numberOfClassesExpiry = async (studentId:any, courseId:any) => {
    try {
        let url = API_URLS.numberOfClassesExpiry;
        url = url
       .replace('{studentId}', studentId)
       .replace('{courseId}', courseId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Students', error);
        throw error;
    }
};

export const getReferral = async (pagesize: number, order: string, status?: string, lastEvaluatedKey?: string, searchTerm?: string) => {
    try {
        let url = API_URLS.getReferral
            .replace('{pagesize}', pagesize.toString())
            .replace('{order}', order);

        // Add optional parameters if they exist
        const queryParams = new URLSearchParams();
        if (status) queryParams.append('status', status);
        if (lastEvaluatedKey) queryParams.append('lastEvaluatedKey', lastEvaluatedKey);
        if (searchTerm) queryParams.append('searchTerm', searchTerm);

        if (queryParams.toString()) {
            url += `&${queryParams.toString()}`;
        }

        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Referral', error);
        throw error;
    }
};

export const referralAction = async (data: any) => {
    try {
        const response = await axios.post(API_URLS. referralAction.replace('{userId}',data.userId), data);
        return response;
    } catch (error) {
        console.error('Error doing action :', error);
        throw error;
    }
};

export const generateQaReport = async (fromDate: string, toDate: string, teacherId?: string) => {
    try {
        // Start with base URL with required params
        let url = `${WEB_API_BASEURL}/admin/QAreport?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;
        
        // Conditionally add teacherId if provided
        if (teacherId) {
            url += `&teacherId=${encodeURIComponent(teacherId)}`;
        }

        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching QA Report:', error);
        throw error;
    }
};
export const changeTeacher = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.changeTeacher, data);
        return response;
    } catch (error) {
        console.error('Error adding:', error);
        throw error;
    }
};

export const changeOfflineTeacher = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.changeOfflineTeacher, data);
        return response;
    } catch (error) {
        console.error('Error changing offline teacher:', error);
        throw error;
    }
};

export const courseListOfStudent = async (studentId: any,) => {
    try {
        let url = API_URLS.courseListOfStudent;
        url = url
        .replace('{studentId}', studentId)
  
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching calendar details', error);
        throw error;
    }
};

export const totalMovingClassPrice = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.totalMovingClassPrice, data);
        return response;
    } catch (error) {
        console.error('Error adding:', error);
        throw error;
    }
};

export const changeCourse = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.changeCourse, data);
        if (response.status === 200) {
            return response.data; // Return only the data on success
        } else {
            return null; // Return null on failure
        }
    } catch (error) {
        console.error('Error adding:', error);
        throw error;
    }
};

// Transaction-based Course Change (new flow) -> payment-service /api/payment/v2/transaction-course-change
export const transactionCourseChange = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.transactionCourseChange, data);
        if (response.status === 200) {
            return response.data;
        }
        return null;
    } catch (error) {
        console.error('Error in transactionCourseChange:', error);
        throw error;
    }
};

export const courseTransfer = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.courseTransfer, data);
        if (response.status === 200) {
            return response.data; // Return only the data on success
        } else {
            return null; // Return null on failure
        }
    } catch (error) {
        console.error('Error adding:', error);
        throw error;
    }
};

export const blockRnr = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.blockRnr, data);
        if (response.status === 200) {
            return response.data; 
        } else {
            return null; 
        }
    } catch (error) {
        console.error('Error adding:', error);
        throw error;
    }
};

export const RnrHistory = async (fromDate: any,toDate:any) => {
    try {
        let url = API_URLS.RnrHistory;
        url = url
        .replace('{fromDate}', fromDate)
        .replace('{toDate}', toDate);

  
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching calendar details', error);
        throw error;
    }
};

// export const updateWorkingHours = async (updatedData: { teacherId: string; weekDay: string; hours: string }[]) => {
//     try {
//         const url = API_URLS.updateWorkingHours;
        
//         console.log("Sending Data:", updatedData);

//         const response = await axios.post(url, updatedData);
//         return response.data;
//     } catch (error) {
//         console.error("Error updating working hours:", error);
//         throw error;
//     }
// };
export const Penalty = async (data: {
    teachetId: string;
    studentId: string;
    classId: string;
    teacherslotId: string;
  }) => {
    try {
      const response = await axios.post(API_URLS.penalty, data, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` // or your auth token getter
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error applying penalty:', error);
      throw error;
    }
  };

  

  export const studentPenalty = async (data: {
    teachetId: string;
    studentId: string;
    classId: string;
    teacherslotId: string;
  }) => {
    try {
      const response = await axios.post(API_URLS.studentPenalty, data, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` // or your auth token getter
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error applying penalty:', error);
      throw error;
    }
  };


export const markAsJoin = async (classId:any, action: string) => {
    try {
        let url = API_URLS.markAsJoin
            .replace('{classId}', encodeURIComponent(classId))
            .replace('{action}', encodeURIComponent(action));

        const response = await axios.put(url, {
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response.data;
    } catch (error) {
        console.error("Error marking as join:", error);
        throw error;
    }
};

export const updateClassStatusToNoShow = async (classId: string, classStatus: string) => {
    try {
        let url = API_URLS.scheduleToNoShow
            .replace('{classId}', encodeURIComponent(classId))
            .replace('{classStatus}', encodeURIComponent(classStatus));

        const response = await axios.get(url, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem('token')}`
            },
        });

        return response.data;
    } catch (error) {
        console.error("Error updating class status to no-show:", error);
        throw error;
    }
};

export const classDetailAPI = async (studentId:any, courseId:any) => {
    try {
        let url = API_URLS.classDetails.replace('{studentId}', studentId)
        .replace('{courseId}', courseId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher details', error);
        throw error;
    }
  }

  export const checkPolicy = async (studentId:any, courseId:any) => {
    try {
        let url = API_URLS.checkPolicy.replace('{studentId}', studentId)
        .replace('{courseId}', courseId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('error fetching policy', error);
        throw error;
    }
  }

  export const getCertificate = async (userId:any, courseId:any) => {
    try {
        let url = API_URLS.getCertificate.replace('{userId}', userId)
        .replace('{courseId}', courseId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('error fetching policy', error);
        throw error;
    }
  }

  export const reGenerateCertificate = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.reGenerateCertificate, data);
        return response;
    } catch (error) {
        console.error('Error fetching student for ae:', error);
        throw error;
    }
};
export const getCourseLevel = async (courseId:any) => {
    try {
        let url = API_URLS.getCourseLevel.replace('{courseId}', courseId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching course level:', error);
        throw error;
    }
};

export const getLevelLesson = async (levelId:any) => {
    try {
        let url = API_URLS.getLevelLesson.replace('{levelId}', levelId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching level lesson:', error);
        throw error;
    }
};
export const getStatusOfLevel = async (studentUserId:any, courseId:any) => {
    if (!isValidApiId(studentUserId) || !isValidApiId(courseId)) {
        return { status: [] };
    }
    try {
        let url = API_URLS.getStatusOfLevel.replace('{studentUserId}', studentUserId).replace('{courseId}', courseId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching status of level:', error);
        throw error;
    }
};

export const completedActiveLevelLessons = async (studentUserId:any, levelId:any) => {
    try {
        let url = API_URLS.completedActiveLevelLessons.replace('{studentUserId}', studentUserId).replace('{levelId}', levelId);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching completed active level lessons:', error);
        throw error;
    }
};

export const markAsCompleteLesson = async (data: any) => {
    try {
        const response = await axios.post(API_URLS.markAsCompleteLesson, data);
        return response;
    } catch (error) {
        console.error('Error fetching student for ae:', error);
        throw error;
    }
};

export const getClassId = async (studentUserId:any, courseId:any) => {
    try {
        let url = API_URLS.getClassId.replace('{studentUserId}', studentUserId).replace('{courseId}', courseId);
        const response = await axios.get(url);
        
        // Handle 204 No Content response
        if (response.status === 204) {
            return { noContent: true, data: [] };
        }
        
        return response.data;
    } catch (error: any) {
        // Check if error response is 204
        if (error.response && error.response.status === 204) {
            return { noContent: true, data: [] };
        }
        console.error('Error fetching class list:', error);
        throw error;
    }
};

export const getAhaMoment = async (
    pageSize: number = 20, 
    lastEvaluatedKey?: string,
    filters?: {
        courseId?: string;
        studentGender?: string;
        studentAgeGroup?: string;
        ratings?: number;
    },
    dateRange?: {
        startDate?: string;
        endDate?: string;
    }
) => { 
    try {
        let url = API_URLS.getAhaMoment;
        
        // Add pagination if lastEvaluatedKey exists
        if (lastEvaluatedKey) {
            url += `&lastEvaluatedKey=${encodeURIComponent(lastEvaluatedKey)}`;
        }

        // Build request body
        const requestBody: any = {};
        
        if (filters && Object.keys(filters).length > 0) {
            requestBody.filter = filters;
        }
        
        if (dateRange && dateRange.startDate && dateRange.endDate) {
            requestBody.date = {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            };
        }

        const response = await axios.post(url, requestBody);
        return response.data;
    } catch (error) {
        console.error('Error fetching aha moment:', error);
        throw error;
    }
};

export const submitAhaMomentRating = async (data: {
    fileName: string;
    userId: string;
    ratings: number;
    user: string;
}) => {
    try {
        const response = await axios.post(`${API_URLS.getAhaMoment.split('?')[0].replace('/ahamoments', '/aha-moment/rating')}`, data);
        return response.data;
    } catch (error) {
        console.error('Error submitting rating:', error);
        throw error;
    }
};

export const getUserConsent = async (userId: string) => {
    try {
        const response = await axios.get(`${WEB_API_BASEURL}/users/user/details?userId=${userId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching user consent:', error);
        throw error;
    }
};

export const getEventCount = async (pageSize: number = 20, lastEvaluatedKey?: string) => {
    try {
        let url = `${API_URLS.getEventCount}?pageSize=${pageSize}`;
        
        // Add pagination if lastEvaluatedKey exists
        if (lastEvaluatedKey) {
            url += `&lastEvaluatedKey=${encodeURIComponent(lastEvaluatedKey)}`;
        }
        
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching event count:', error);
        throw error;
    }
};

export const getCadenceSchedule = async (filters?: { fromDate?: string; toDate?: string }) => {
    try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        if (filters?.fromDate) params.set('fromDate', filters.fromDate);
        if (filters?.toDate) params.set('toDate', filters.toDate);
        const url = params.toString() ? `${API_URLS.getCadenceSchedule}?${params.toString()}` : API_URLS.getCadenceSchedule;
        const response = await axios.get(url, {
            headers: {
                Accept: 'application/json, text/plain, */*',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching cadence schedule:', error);
        throw error;
    }
};

export const createCommunityBroadcast = async (data: any) => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.post(API_URLS.createCommunityBroadcast, data, {
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error creating community broadcast:', error);
        throw error;
    }
};

export const updateCommunityBroadcast = async (scheduleId: string, data: any) => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.put(`${API_URLS.updateCommunityBroadcast}/${scheduleId}`, data, {
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error updating community broadcast:', error);
        throw error;
    }
};

export const deleteCommunityBroadcast = async (scheduleId: string) => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_URLS.deleteCommunityBroadcast}/${scheduleId}`, {
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting community broadcast:', error);
        throw error;
    }
};

export const processCommunityUpload = async (file: File, uploadedBy: string) => {
    try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('uploadedBy', uploadedBy);
        const response = await axios.post(API_URLS.processCommunityUpload, formData, {
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading community sheet:', error);
        throw error;
    }
};

export const generateZoomLink = async (classId:any)=> {
    try {
        let url = API_URLS.generateZoomLink.replace('{classId}', classId);
        const response = await axios.post(url);
        return response.data;
    } catch (error) {
        console.error('Error generating zoom link:', error);
        throw error;
    }
}

export const getZoomRecordings = async (messageId: string) => {
    try {
        const url = `${API_URLS.getZoomRecordings}?messageId.equals=${messageId}`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching zoom recordings:', error);
        throw error;
    }
};

export const updateIs30MinsClass = async (
    studentUserId: string | number,
    courseId: string | number,
    is30Mins: boolean,
    reason: string
) => {
    try {
        let url = API_URLS.updateIs30MinsClass
            .replace('{studentUserId}', String(studentUserId))
            .replace('{courseId}', String(courseId))
            .replace('{is30Mins}', is30Mins.toString())
            .replace('{reason}', encodeURIComponent(reason));
        const response = await axios.put(url, { reason });
        return response;
    } catch (error) {
        console.error('Error updating is30MinsClass', error);
        throw error;
    }
};

export const addCommunity = async (data: { studentUserId: string; teacherUserId: string; courseId: string }) => {
    try {
        const response = await axios.post(API_URLS.addCommunity, data, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        return response;
    } catch (error) {
        console.error('Error adding student to community:', error);
        throw error;
    }
};

const TRANSACTION_BY_CREATED_DATE_URL =
    process.env.REACT_APP_TRANSACTION_BASEURL
        ? `${process.env.REACT_APP_TRANSACTION_BASEURL.replace(/\/$/, '')}/api/payment/transactionbycreateddate`
        : 'https://k02w0xzzu3.execute-api.ap-south-1.amazonaws.com/api/payment/transactionbycreateddate';

export const getTransactionsByCreatedDate = async (fromDate: string, toDate: string, isOffline: string = "false") => {
    try {
        const response = await axios.post(TRANSACTION_BY_CREATED_DATE_URL, {
            fromDate,
            toDate,
            isOffline,
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching transactions:', error);
        throw error;
    }
};

export const removeCommunity = async (data: { studentUserId: string; courseId: string }) => {
    try {
        const response = await axios.delete(API_URLS.removeCommunity, {
            data: data,
            headers: {
                "Content-Type": "application/json",
            },
        });
        return response;
    } catch (error) {
        console.error('Error removing student from community:', error);
        throw error;
    }
};
export const getQaScore = async (teacherUserId:any) => {
    try {
        let url = API_URLS.getQAScore;
        url = url.replace('{teacherUserId}', teacherUserId);
        
        // Get JWT token from cookies
        const getCookie = (name: string): string | null => {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
            return null;
        };
        
        const token = getCookie('jwtToken');
        const authToken = token?.startsWith('Bearer ') ? token : `Bearer ${token}`;
        
        const response = await axios.get(url, {
            headers: {
                'Authorization': authToken,
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching QA Score:', error);
        throw error;
    }
};

export type TeacherLedgerLine = {
    id: string | number;
    ledgerTab?: string;
    auditAction?: string | null;
    disputeStatus?: string | null;
    adjustmentStatus?: string | null;
    teacherId?: string | number | null;
    amount?: number;
    classDate?: string;
    occurredAt?: string;
    classtime?: string;
    studentDisplayName?: string;
    courseDisplayName?: string;
    classLevel?: string;
    domestic?: boolean;
    INTL1?: boolean;
    INTL2?: boolean;
    classtimeDiffs?: number;
    uiFeeLabel?: string;
    pendingReview?: boolean;
    entryType?: string;
    communityStudentCount?: number | string | null;
    reason?: string | null;
    description?: string | null;
    notes?: string | null;
};

export type TeacherLedgerResponse = {
    lines?: TeacherLedgerLine[];
    ledgerTabs?: string[];
    summary?: Record<string, unknown>;
};

export const getTeacherLedger = async (
    teacherUserId: string,
    fromDate: string,
    toDate: string
): Promise<TeacherLedgerResponse> => {
    try {
        let url = API_URLS.teacherLedger;
        url = url
            .replace('{teacherUserId}', encodeURIComponent(teacherUserId))
            .replace('{fromDate}', fromDate)
            .replace('{toDate}', toDate);
        const response = await axios.get<TeacherLedgerResponse>(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching teacher ledger:', error);
        throw error;
    }
};

export type TeacherLedgerAdjustmentType = "CREDIT" | "DEDUCTION";

export type TeacherLedgerAdjustmentPayload =
    | {
          action: "UPDATE_LINE";
          ledgerLineId: number;
          teacherId: number;
          update: { amount: number; notes?: string };
          reason: string;
          description?: string;
          classTiming?: 30 | 60;
      }
    | {
          action: "CREATE_LINE";
          line: { teacherId: number; amount: number };
          reason: string;
          description?: string;
          classTiming?: 30 | 60;
      }
    | {
          action: "REJECT_DISPUTE";
          ledgerLineId: number;
          teacherId: number;
          reason: string;
      };

export const rejectTeacherLedgerDispute = async (payload: {
    ledgerLineId: number;
    teacherId: number;
    reason: string;
}) => {
    try {
        const response = await axios.post(API_URLS.teacherLedgerAdjustment, {
            action: "REJECT_DISPUTE",
            ...payload,
        });
        return response.data;
    } catch (error) {
        console.error("Error rejecting teacher ledger dispute:", error);
        throw error;
    }
};

export const submitTeacherLedgerAdjustment = async (payload: TeacherLedgerAdjustmentPayload) => {
    try {
        const response = await axios.post(API_URLS.teacherLedgerAdjustment, payload);
        return response.data;
    } catch (error) {
        console.error("Error submitting teacher ledger adjustment:", error);
        throw error;
    }
};
/* -------------------------------------------------------------------------- */
/* Demo Ops (acquisition agents — POST /admin/leads, availability, persistence) */
/* -------------------------------------------------------------------------- */

const demoOpsPlainAxios = axios.create();

export type DemoOpsUpdateAgentLeadPayload = {
  classId: string | number;
  latestDemoStatus?: string;
  DemoCallStatus1?: string;
  DemoCallStatus2?: string;
  notConductedReason?: string;
  comments?: string;
  agentId: string;
};

export type DemoOpsLeadHistoryAuditPayload = {
  id: string;
  field: string;
  user: string;
  user_id: string;
  old_value: string;
  new_value: string;
};

export const postDemoOpsAdminLeads = async (
  body: Record<string, unknown>
): Promise<{
  content?: unknown[];
  totalPages?: number;
  totalElements?: number;
}> => {
  const { data } = await axios.post(API_URLS.demoOpsAdminLeads, body, {
    headers: { "Content-Type": "application/json" },
  });
  return data ?? {};
};

export const getDemoOpsAgentStatus = async (
  agentId: string
): Promise<{ status: string; timings: DemoOpsShiftRow[] }> => {
  let url = API_URLS.demoOpsAgentStatus;
  url = url.replace("{agentId}", encodeURIComponent(String(agentId)));
  const { data } = await axios.get(url);
  if (Array.isArray(data)) {
    const anyActive = data.some(
      (r: { status?: string }) => String(r?.status ?? "").toLowerCase() === "active"
    );
    return { status: anyActive ? "active" : "inactive", timings: data };
  }
  const inner =
    typeof data === "object" && data !== null && "data" in data
      ? (data as { data?: { status?: string } }).data
      : (data as { status?: string } | undefined);
  return { status: String(inner?.status ?? "").toLowerCase(), timings: [] };
};

export const postDemoOpsAgentAvailability = async (
  agentId: string,
  status: "active" | "inactive"
): Promise<void> => {
  await axios.post(API_URLS.demoOpsAgentAvailability, { agentId, status }, {
    headers: { "Content-Type": "application/json" },
  });
};

export const postDemoOpsAgentShiftStatus = async (
  agentId: string,
  status: "active" | "inactive",
  shiftId: string
): Promise<void> => {
  await axios.post(API_URLS.demoOpsAgentShiftStatus, { agentId, status, shiftId }, {
    headers: { "Content-Type": "application/json" },
  });
};

export const updateDemoOpsAgentLead = async (
  body: DemoOpsUpdateAgentLeadPayload
): Promise<void> => {
  await axios.post(API_URLS.demoOpsUpdateAgentLead, body, {
    headers: { "Content-Type": "application/json" },
  });
};

export const getDemoOpsDemoClassLink = async (
  classId: string | number,
  studentId: string | number
): Promise<string> => {
  let url = API_URLS.demoOpsDemoClassLink;
  url = url.replace("{classId}", encodeURIComponent(String(classId)));
  url = url.replace("{studentId}", encodeURIComponent(String(studentId)));
  const { data } = await axios.get(url, { responseType: "text" });
  return typeof data === "string" ? data.trim() : String(data ?? "");
};

export const postDemoOpsLeadHistoryAudit = async (
  item: DemoOpsLeadHistoryAuditPayload
): Promise<void> => {
  if (!DEMOOPS_HISTORY_BASEURL) return;
  await axios.post(API_URLS.demoOpsLeadHistory, item, {
    headers: { "Content-Type": "application/json" },
  });
};

export const fetchDemoOpsLeadHistory = async (
  studentId: string
): Promise<
  { field?: string; old_value?: string; new_value?: string; createdOn?: string }[]
> => {
  if (!DEMOOPS_HISTORY_BASEURL) return [];
  let url = API_URLS.demoOpsLeadHistoryByStudent;
  url = url.replace("{studentId}", encodeURIComponent(studentId));
  const { data } = await axios.get<{
    data?: { Items?: Record<string, unknown>[] };
  }>(url);
  const items = data?.data?.Items ?? [];
  return items.map((row) => ({
    field: String(row.field ?? ""),
    old_value: String(row.old_value ?? ""),
    new_value: String(row.new_value ?? ""),
    createdOn:
      row.createdOn != null ? String(row.createdOn) : undefined,
  }));
};

export const postDemoOpsClickToCall = async (body: {
  agent: string;
  customer: string;
  userName: string;
}): Promise<void> => {
  await demoOpsPlainAxios.post(API_URLS.demoOpsClickToCall, body, {
    headers: { "Content-Type": "application/json" },
  });
};

export type DemoOpsAgentListItem = {
  id?: string | number;
  agentId?: string | number;
  agentName?: string;
  role?: string;
  status?: string;
};

export type DemoOpsShiftRow = {
  id?: string | number;
  agentId?: string | number;
  agentName?: string;
  role?: string;
  status?: string;
  day?: string;
  shiftStartTime?: string;
  shiftEndTime?: string;
};

export const getDemoOpsAgentDetails = async (
  agentId: string
): Promise<{ status?: string; role?: string }> => {
  let url = API_URLS.demoOpsAgentStatus;
  url = url.replace("{agentId}", encodeURIComponent(String(agentId)));
  const { data } = await axios.get(url);
  const inner =
    typeof data === "object" && data !== null && "data" in data
      ? (data as { data?: { status?: string; role?: string } }).data
      : (data as { status?: string; role?: string } | undefined);
  return {
    status: String(inner?.status ?? "").toLowerCase(),
    role: String(inner?.role ?? ""),
  };
};

export const getDemoOpsAgents = async (): Promise<DemoOpsAgentListItem[]> => {
  const { data } = await axios.get(API_URLS.demoOpsFindAgents);
  return Array.isArray(data) ? data : [];
};

export const getDemoOpsAgentShifts = async (
  agentId: string | number
): Promise<DemoOpsShiftRow[]> => {
  const { data } = await axios.get(API_URLS.demoOpsFindAgents, {
    params: { agentId },
  });
  return Array.isArray(data) ? data : [];
};

export const getDemoOpsActiveShifts = async (
  days: string[]
): Promise<DemoOpsShiftRow[]> => {
  const { data } = await axios.get(API_URLS.demoOpsActiveShifts, {
    params: { day: days.join(",") },
    paramsSerializer: (params) => `day=${params.day}`,
  });
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.content) ? data.content : [];
};

export const addDemoOpsAgentShift = async (body: {
  agentId: string | number;
  agentName: string;
  role: string;
  status: string;
  day: string;
  shiftStartTime: string;
  shiftEndTime: string;
}): Promise<void> => {
  await axios.post(API_URLS.demoOpsAddDeleteAgents, body, {
    headers: { "Content-Type": "application/json" },
  });
};

export const deleteDemoOpsAgentShift = async (
  entryId: string | number
): Promise<void> => {
  await axios.post(
    API_URLS.demoOpsAddDeleteAgents,
    {},
    {
      params: { entryId },
      headers: { "Content-Type": "application/json" },
    }
  );
};

export const updateDemoOpsAgentShift = async (body: {
  agentId: string | number;
  entryId: string | number;
  day: string;
  shiftStartTime: string;
  shiftEndTime: string;
}): Promise<void> => {
  await axios.put(API_URLS.demoOpsUpdateAgentDetails, body, {
    headers: { "Content-Type": "application/json" },
  });
};



// ── AE/TE Slots Dashboard (artium-admin-service /api/v1/dashboard) ──

export type DashboardRole = "all" | "AE" | "TE";
export type DashboardGranularity = "hourly" | "daywise";
/** Conduct-% colour band (server-owned). NOBOOK = had supply but nothing booked. */
export type ConductBucket = "EMPTY" | "NOBOOK" | "CRIT" | "WARN" | "GOOD";
/** Capacity-fill colour band (server-owned). Reads inversely to ConductBucket. */
export type BookedBucket = "EMPTY" | "GREEN" | "AMBER" | "RED";

export interface DashboardFilters {
    from: string;
    to: string;
    role?: DashboardRole;
    categoryId?: number[];
    teacherId?: number[];
    granularity?: DashboardGranularity;
}

/** Structured 400 body returned by the dashboard service on validation failure. */
export interface DashboardError {
    error: string;
    message: string;
    field?: string;
    fix?: string;
}

export interface MetricGroup {
    available: number;
    booked: number;
    conducted: number;
    /** conducted/booked %, null when booked = 0. */
    conductPct: number | null;
    conductBucket: ConductBucket;
    /** booked/available %, null when available = 0. */
    bookedPct: number | null;
    bookedBucket: BookedBucket;
}

export interface HeatmapCountries {
    india?: MetricGroup | null;
    us?: MetricGroup | null;
    row?: MetricGroup | null;
}

export interface HeatmapCell extends MetricGroup {
    window: string;
    ae?: MetricGroup;
    te?: MetricGroup;
    intl?: MetricGroup;
    dom?: MetricGroup;
    countries?: HeatmapCountries | null;
}

export interface HeatmapRow {
    date: string;
    dow: string;
    /** Distinct people holding a slot that date — headcount, not slot count. */
    teacherCount: number;
    aeCount: number;
    teCount: number;
    cells: HeatmapCell[];
}

export type DaywiseMetric = MetricGroup;

export interface DaywiseRow {
    date: string;
    dow: string;
    /** Combined intl + domestic metrics for the day. */
    total?: DaywiseMetric;
    intl: DaywiseMetric;
    dom: DaywiseMetric;
    /** India / US / ROW split for the day (All courses). */
    countries?: HeatmapCountries | null;
    byCategory: Array<{
        categoryId: number;
        categoryName: string;
        /** Combined intl + domestic metrics for this category on the day. */
        total?: DaywiseMetric;
        intl: DaywiseMetric;
        dom: DaywiseMetric;
        /** India / US / ROW split for this category on the day. */
        countries?: HeatmapCountries | null;
    }>;
}

export interface DashboardCategory {
    id: number;
    name: string;
}

export interface HeatmapResponse {
    from: string;
    to: string;
    role: DashboardRole;
    view: string;
    granularity: DashboardGranularity;
    categoryIds: number[];
    teacherIds: number[];
    timeWindows: string[];
    rows: HeatmapRow[] | null;
    daywise: DaywiseRow[] | null;
    totals: {
        perWindow: HeatmapCell[];
        overall: HeatmapCell;
        /** Range totals split per category — day-wise only. Absent on hourly responses. */
        byCategory?: Array<{
            categoryId: number;
            categoryName: string;
            total: MetricGroup;
            intl: MetricGroup;
            dom: MetricGroup;
            countries?: HeatmapCountries | null;
        }>;
        /** India / US / ROW for the overall range total — day-wise only. */
        countries?: HeatmapCountries | null;
    };
    categories: DashboardCategory[];
}

export interface TeacherGridCell {
    window: string;
    slot: "intl" | "dom" | "both" | null;
    /** That teacher's own metrics for the window; null when no supply/demand there. */
    metrics: MetricGroup | null;
    countries?: HeatmapCountries | null;
}

export interface TeacherGridRow {
    teacherId: number;
    role: "AE" | "TE";
    name: string;
    category: string;
    paidStudents: number | null;
    cells: TeacherGridCell[];
}

export interface TeacherGridResponse {
    from: string;
    to: string;
    role: DashboardRole;
    granularity: DashboardGranularity;
    categoryIds: number[];
    teacherIds: number[];
    timeWindows: string[];
    teachers: TeacherGridRow[];
    /** Per-window headcount: a teacher with both an intl and a dom slot counts once. */
    totalsPerWindow: Array<{ window: string; teacherCount: number; aeCount: number; teCount: number }>;
}

export interface MarketMetrics {
    joined: number;
    conducted: number;
    converted: number;
    conductPct: number | null;
    convertPct: number | null;
    /** AE/TE share of `conducted`. Only on markets/totals — null on people[] rows. */
    aeConducted?: number | null;
    teConducted?: number | null;
    /** Funnel head: trials scheduled before anyone joined. Absent on people[] rows. */
    totalScheduled?: number | null;
    /** Joined trials that never got an AE assigned — leakage, not a funnel stage. */
    noAeFound?: number | null;
    /** joined/totalScheduled %, null when nothing was scheduled. */
    joinPct?: number | null;
}

export interface ConductConvertPerson {
    teacherId: number;
    role: "AE" | "TE";
    name: string;
    category: string;
    categoryId: number;
    paidStudents: number | null;
    us: MarketMetrics;
    row: MarketMetrics;
    dom: MarketMetrics;
    total: MarketMetrics;
}

export interface ConductConvertResponse {
    from: string;
    to: string;
    role: DashboardRole;
    funnel: {
        /** Funnel head: trials scheduled before anyone joined. */
        totalScheduled?: number | null;
        joined: number;
        conducted: number;
        converted: number;
        /** Joined trials that never got an AE assigned — leakage, not a funnel stage. */
        noAeFound?: number | null;
        /** joined/totalScheduled %, null when nothing was scheduled. */
        joinPct?: number | null;
        conductPct: number | null;
        convertPct: number | null;
    };
    markets: { us: MarketMetrics; row: MarketMetrics; dom: MarketMetrics; total: MarketMetrics };
    people: ConductConvertPerson[];
    totals: { us: MarketMetrics; row: MarketMetrics; dom: MarketMetrics; total: MarketMetrics };
    peopleCount: number;
    categories: DashboardCategory[];
}

/** Build the shared dashboard query string. List ids are sent comma-separated. */
const buildDashboardQuery = (filters: DashboardFilters): string => {
    const params = new URLSearchParams();
    params.set("from", filters.from);
    params.set("to", filters.to);
    if (filters.role) params.set("role", filters.role);
    if (filters.granularity) params.set("granularity", filters.granularity);
    if (filters.categoryId && filters.categoryId.length) params.set("categoryId", filters.categoryId.join(","));
    if (filters.teacherId && filters.teacherId.length) params.set("teacherId", filters.teacherId.join(","));
    return params.toString();
};

export const getDashboardSlotHeatmap = async (filters: DashboardFilters): Promise<HeatmapResponse> => {
    try {
        const url = `${API_URLS.dashboardSlotHeatmap}?${buildDashboardQuery(filters)}`;
        const response = await axios.get<HeatmapResponse>(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching slot heatmap:", error);
        throw error;
    }
};

export const getDashboardSlotHeatmapTeachers = async (filters: DashboardFilters): Promise<TeacherGridResponse> => {
    try {
        const url = `${API_URLS.dashboardSlotHeatmapTeachers}?${buildDashboardQuery(filters)}`;
        const response = await axios.get<TeacherGridResponse>(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching teacher slot grid:", error);
        throw error;
    }
};

export const getDashboardConductConvert = async (filters: DashboardFilters): Promise<ConductConvertResponse> => {
    try {
        const url = `${API_URLS.dashboardConductConvert}?${buildDashboardQuery(filters)}`;
        const response = await axios.get<ConductConvertResponse>(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching conduct-convert funnel:", error);
        throw error;
    }
};

/* -------------------------------------------------------------------------- */
/* Teacher Escalation                                                         */
/* -------------------------------------------------------------------------- */


export type TeacherEscalationValidationStatus = "PENDING" | "VALID" | "INVALID";

export type TeacherEscalation = {
    id: number;
    freshdeskTicketId?: number | null;
    ticketType?: string | null;
    teacherUserId?: number | null;
    teacherName?: string | null;
    teacherMobileNumber?: string | null;
    studentUserId?: number | null;
    category?: string | null;
    subcategory?: string | null;
    validationStatus?: TeacherEscalationValidationStatus | string | null;
    validationNotes?: string | null;
    link?: string | null;
    description?: string | null;
    freshdeskStatus?: string | null;
    priority?: string | null;
    isDeleted?: boolean;
    createdBy?: string | null;
    createdOn?: string | null;
    editedBy?: string | null;
    editedOn?: string | null;
    actionNotes?: string | null;
    /** @deprecated use actionNotes */
    actionTaken?: string | null;
    courseCategory?: string | null;
    teacherGenre?: string | null;
    genre?: { id?: number | null; name?: string | null } | null;
};

export type TeacherEscalationListParams = {
    validationStatus?: TeacherEscalationValidationStatus | string;
    teacherUserId?: number | string;
    studentUserId?: number | string;
    category?: string;
    freshdeskTicketId?: number | string;
    freshdeskStatus?: string;
    genreId?: number | string;
    actionNotes?: string;
    createdFrom?: string;
    createdTo?: string;
    editedFrom?: string;
    editedTo?: string;
    includeDeleted?: boolean;
    sortBy?: string;
    sortOrder?: "asc" | "desc" | string;
    page?: number;
    size?: number;
};

export type TeacherEscalationListResponse = {
    content: TeacherEscalation[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    stats?: TeacherEscalationStats;
};

export type TeacherEscalationByTeacherParams = {
    flagThreshold?: number | string;
    flaggedOnly?: boolean;
    sortBy?: string;
    page?: number;
    size?: number;
    teacherUserId?: number | string;
    genreId?: number | string;
    category?: string;
    validationStatus?: TeacherEscalationValidationStatus | string;
    createdFrom?: string;
    createdTo?: string;
};

export type TeacherEscalationByTeacherRow = {
    teacherUserId: number;
    teacherName?: string | null;
    teacherMobileNumber?: string | null;
    genre?: { id?: number | null; name?: string | null } | null;
    all: number;
    valid: number;
    invalid: number;
    open: number;
    lastEscalationOn?: string | null;
    flagged: boolean;
};

export type TeacherEscalationByTeacherResponse = {
    content: TeacherEscalationByTeacherRow[];
    flagThreshold: number;
    flaggedTeachers: number;
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
};

export type TeacherEscalationStats = {
    open: number;
    validatedValid: number;
    validatedInvalid: number;
    resolvedThisMonth: number;
    total: number;
};

export const normalizeTeacherEscalationStats = (
    raw?: Record<string, unknown> | null
): TeacherEscalationStats => ({
    open: Number(raw?.open ?? 0),
    validatedValid: Number(raw?.validatedValid ?? 0),
    validatedInvalid: Number(raw?.validatedInvalid ?? 0),
    resolvedThisMonth: Number(raw?.resolvedThisMonth ?? raw?.resolved ?? 0),
    total: Number(raw?.total ?? 0),
});

export type CreateTeacherEscalationBody = {
    teacherUserId?: number;
    studentUserId?: number;
    category?: string;
    subcategory?: string;
    description?: string;
    link?: string;
    ticketType?: string;
    priority?: string;
    freshdeskStatus?: string;
    freshdeskTicketId?: number;
    createdBy?: string;
    /** PRD fields — backend may ignore until supported */
    actionTaken?: string;
    courseCategory?: string;
    teacherGenre?: string;
};

export type ValidateTeacherEscalationBody = {
    validationStatus: TeacherEscalationValidationStatus | string;
    validationNotes?: string;
    validatedBy?: string;
};

export type RecordTeacherEscalationActionBody = {
    actionNotes: string;
};

export const getTeacherEscalationByTeacher = async (
    params: TeacherEscalationByTeacherParams = {}
): Promise<TeacherEscalationByTeacherResponse> => {
    try {
        const response = await axios.get(API_URLS.teacherEscalationByTeacher, { params });
        const data = response.data || {};
        const content = Array.isArray(data.content) ? data.content : [];
        return {
            content: content.map((row: any): TeacherEscalationByTeacherRow => ({
                teacherUserId: Number(row?.teacherUserId),
                teacherName: row?.teacherName ?? null,
                teacherMobileNumber: row?.teacherMobileNumber ?? null,
                genre: row?.genre ?? null,
                all: Number(row?.all ?? 0),
                valid: Number(row?.valid ?? 0),
                invalid: Number(row?.invalid ?? 0),
                open: Number(row?.open ?? 0),
                lastEscalationOn: row?.lastEscalationOn ?? null,
                flagged: Boolean(row?.flagged),
            })),
            flagThreshold: Number(data.flagThreshold ?? params.flagThreshold ?? 2),
            flaggedTeachers: Number(data.flaggedTeachers ?? 0),
            page: Number(data.page ?? params.page ?? 0),
            size: Number(data.size ?? params.size ?? 200),
            totalElements: Number(data.totalElements ?? content.length ?? 0),
            totalPages: Number(data.totalPages ?? 0),
        };
    } catch (error) {
        console.error("Error fetching teacher escalations by teacher", error);
        throw error;
    }
};

export const getTeacherEscalationList = async (
    params: TeacherEscalationListParams = {}
): Promise<TeacherEscalationListResponse> => {
    try {
        const response = await axios.get(API_URLS.teacherEscalationList, { params });
        const data = response.data || {};
        return {
            content: Array.isArray(data.content) ? data.content : [],
            page: Number(data.page ?? params.page ?? 0),
            size: Number(data.size ?? params.size ?? 20),
            totalElements: Number(data.totalElements ?? 0),
            totalPages: Number(data.totalPages ?? 0),
            stats: data.stats ? normalizeTeacherEscalationStats(data.stats) : undefined,
        };
    } catch (error) {
        console.error("Error fetching teacher escalations", error);
        throw error;
    }
};

export const getTeacherEscalationStats = async (): Promise<TeacherEscalationStats> => {
    try {
        const response = await axios.get(API_URLS.teacherEscalationStats);
        return normalizeTeacherEscalationStats(response.data || {});
    } catch (error) {
        console.error("Error fetching teacher escalation stats", error);
        throw error;
    }
};

export const getTeacherEscalationById = async (id: number | string): Promise<TeacherEscalation> => {
    try {
        const url = API_URLS.teacherEscalationById.replace("{id}", String(id));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching teacher escalation by id", error);
        throw error;
    }
};

export const getTeacherEscalationByTicketId = async (
    ticketId: number | string
): Promise<TeacherEscalation> => {
    try {
        const url = API_URLS.teacherEscalationByTicket.replace("{ticketId}", String(ticketId));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching teacher escalation by ticket id", error);
        throw error;
    }
};

export const createTeacherEscalation = async (
    body: CreateTeacherEscalationBody
): Promise<TeacherEscalation> => {
    try {
        const response = await axios.post(API_URLS.teacherEscalationCreate, body);
        return response.data;
    } catch (error) {
        console.error("Error creating teacher escalation", error);
        throw error;
    }
};

export const validateTeacherEscalation = async (
    id: number | string,
    body: ValidateTeacherEscalationBody
): Promise<TeacherEscalation> => {
    try {
        const url = API_URLS.teacherEscalationValidate.replace("{id}", String(id));
        const response = await axios.post(url, body);
        return response.data;
    } catch (error) {
        console.error("Error validating teacher escalation", error);
        throw error;
    }
};

export const recordTeacherEscalationAction = async (
    id: number | string,
    body: RecordTeacherEscalationActionBody
): Promise<TeacherEscalation> => {
    try {
        const url = API_URLS.teacherEscalationAction.replace("{id}", String(id));
        const response = await axios.post(url, body);
        return response.data;
    } catch (error) {
        console.error("Error recording teacher escalation action", error);
        throw error;
    }
};

// ------------------------------------------------------------
// Offline Subscription Renewals (Action Centre)
// ------------------------------------------------------------

export type OfflineRenewalBucketKey =
  | "upcomingRenewal"
  | "renewalOverdue"
  | "expired";

export type OfflineRenewalRow = {
  enrollmentId?: string | number;
  studentName?: string;
  name?: string;
  studentCredentialId?: string | number;
  credentialId?: string | number;
  studentId?: string | number;
  courseLevel?: string;
  course?: string;
  courseId?: string | number;
  teacherName?: string;
  teacher?: string;
  teacherId?: string | number;
  centreName?: string;
  centerName?: string;
  centre?: string;
  center?: string;
  centerId?: string | number;
  centreId?: string | number;
  totalClasses?: string | number;
  classesPurchased?: string | number;
  expiryDate?: string;
  accessUntil?: string;
  status?: OfflineRenewalBucketKey | string;
  currentCallDecision?: string;
  currentCallDescription?: string | null;
  callDecisionUpdatedAt?: string | null;
  awaitingCallOutcome?: string;
  // Used by decision select
  callDecisionUpdatedAtIso?: string | null;
  // Decisions API might use this name:
  decision?: string;
  // Keep any additional fields
  [key: string]: unknown;
};

export type OfflineRenewalsResponse = {
  upcomingRenewal?: { data: OfflineRenewalRow[]; count: number };
  renewalOverdue?: { data: OfflineRenewalRow[]; count: number };
  expired?: { data: OfflineRenewalRow[]; count: number };
  totalStudents?: number;
  awaitingCallOutcome?: number;
  [key: string]: unknown;
};

export const getOfflineRenewals = async (
  params: Record<string, unknown> = {}
): Promise<OfflineRenewalsResponse> => {
  try {
    const url = API_URLS.renewalsList;
    const response = await axios.get(url, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching offline renewals:", error);
    throw error;
  }
};

export type PostRenewalCallDecisionBody = {
  decision: string;
  description?: string;
};

export const postOfflineRenewalCallDecision = async (
  enrollmentId: string | number,
  body: PostRenewalCallDecisionBody
): Promise<{
  enrollmentId: string | number;
  currentCallDecision: string;
  callDecisionUpdatedAt: string | null;
}> => {
  try {
    let url = API_URLS.renewalsCallDecisions;
    url = url.replace("{enrollmentId}", String(enrollmentId));
    const response = await axios.post(url, body, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    console.error("Error posting offline renewal call decision:", error);
    throw error;
  }
};

export const getOfflineRenewalCallDecisionsHistory = async (
  enrollmentId: string | number
): Promise<
  Array<{
    id?: number | string;
    decision?: string;
    note?: string | null;
    description?: string | null;
    calledByUserId?: number | string;
    calledByName?: string;
    calledAt?: string;
  }>
> => {
  try {
    let url = API_URLS.renewalsCallDecisions;
    url = url.replace("{enrollmentId}", String(enrollmentId));
    const response = await axios.get(url);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Error fetching offline renewal call decision history:", error);
    throw error;
  }
};

// ─── Split payment ───────────────────────────────────────────────────────────

export const createSplitPackage = async (data: any) => {
  try {
    const response = await axios.post(API_URLS.createSplitPackage, data);
    return response;
  } catch (error) {
    console.error("Error creating split package:", error);
    throw error;
  }
};

export const getSplitPreview = async (data: any) => {
  try {
    const response = await axios.post(API_URLS.splitPreview, data);
    return response;
  } catch (error) {
    console.error("Error fetching split preview:", error);
    throw error;
  }
};

export const updateSplitPackage = async (data: any) => {
  try {
    const response = await axios.post(API_URLS.updateSplitPackage, data);
    return response;
  } catch (error) {
    console.error("Error updating split package:", error);
    throw error;
  }
};


// --- class correction (artium-class-ops-api) --------------------------------

export type ClassCorrectionAction = 'complete' | 'mark-completed';

export interface ClassCorrectionRequest {
    teacherId: string;
    slotDate: string;     // YYYY-MM-DD
    startTime: string;    // HH:MM:SS
    studentMobile: string;
    dryRun: boolean;
}

export interface ClassCorrectionResolved {
    teacher_id: number;
    airline_teacher_slot_id: number;
    airline_booking_slot_id: number;
    slot_date: string;
    start_time: string;
    end_time: string;
    class_schedule_id: number;
    student_class_id: number;
    student_enrollment_id: number;
}

export interface ClassCorrectionResponse {
    status: 'completed' | 'dry_run';
    resolved: ClassCorrectionResolved;
}

export const runClassCorrection = async (
    action: ClassCorrectionAction,
    data: ClassCorrectionRequest
): Promise<ClassCorrectionResponse> => {
    try {
        // the fields travel in the JSON body, never the query string, so the
        // student's mobile number stays out of URLs and access logs
        const body: Record<string, string | number | boolean> = {
            teacher_id: Number(data.teacherId),
            slot_date: data.slotDate,
            start_time: data.startTime,
            student_mobile: data.studentMobile,
            dry_run: data.dryRun,
        };
        // the axios interceptor in App.tsx attaches the admin's login token as
        // the bearer token the class ops API accepts directly
        const url = action === 'complete'
            ? API_URLS.completeTrialClass
            : API_URLS.markTrialClassCompleted;
        const response = await axios.post(url, body);
        return response.data;
    } catch (error) {
        console.error('Error running class correction:', error);
        throw error;
    }
};


// --- Video call QA (artium-videocall-qa) ---------------------------------------------

export type VideoCallQaStatus =
    | 'scheduled' | 'live' | 'recorded' | 'queued' | 'processing' | 'scored'
    | 'no_show' | 'cancelled' | 'failed';

export interface VideoCallQaLesson {
    id: string;
    class_schedule_id: string;
    class_type: string;
    status: VideoCallQaStatus | string;
    scheduled_start: string;
    scheduled_end: string;
    teacher_id: string | null;
    learner_id: string | null;
    instrument: string | null;
    /** Length of the analysed recording, seconds; null until the class has been analysed. */
    call_duration_s?: number | null;
}

export interface VideoCallQaEvidence {
    t_start: number;
    t_end?: number | null;
    speaker?: string | null;
    quote?: string | null;
    frame_key?: string | null;
}

export interface VideoCallQaResult {
    id: string;
    parameter_key: string;
    rubric_version_id: string;
    score: number | null;
    scale: string;
    label: string | null;
    confidence: number | null;
    evidence: VideoCallQaEvidence[];
    missing_elements: string[];
    summary: string | null;
}

export interface VideoCallQaIncident {
    id: string;
    kind: string;
    side: string;
    cause: string | null;
    t_start_s: number;
    t_end_s: number | null;
    lost_seconds: number | null;
    confidence: number | null;
}

export interface VideoCallQaReport {
    /** The QA service calls a session a "class" (one lesson can span several classes). */
    live_class: VideoCallQaLesson;
    media: Record<string, string>;
    timeline_url: string | null;
    results: VideoCallQaResult[];
    incidents: VideoCallQaIncident[];
}

export interface VideoCallQaLessonFilters {
    status?: string;
    since?: string;
    limit?: number;
}

export const getVideoCallQaLessons = async (
    filters: VideoCallQaLessonFilters
): Promise<VideoCallQaLesson[]> => {
    try {
        const params: Record<string, string | number> = { limit: filters.limit ?? 50 };
        if (filters.status) params.status = filters.status;
        if (filters.since) params.since = filters.since;
        const response = await axios.get(API_URLS.videoCallQaLessons, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching video call QA lessons:', error);
        throw error;
    }
};

/** Full review (scores, flags, transcript, presence, recording links) for one class, by
 *  class_schedule_id or class id. Sent with the CMS token by the global axios interceptor. */
/** Manually re-run one class with the current production QA settings (fresh timeline + scores). */
export const reanalyseVideoCallQaClass = async (classKey: string): Promise<{ status: string }> => {
    const response = await axios.post(`${VIDEOCALL_QA_BASEURL}/v1/classes/${encodeURIComponent(classKey)}/reanalyse`);
    return response.data;
};

export const getVideoCallQaReview = async (classKey: string): Promise<QaReview> => {
    const response = await axios.get(`${VIDEOCALL_QA_BASEURL}/v1/review/${encodeURIComponent(classKey)}`);
    return response.data;
};

export const getVideoCallQaReport = async (lessonId: string): Promise<VideoCallQaReport> => {
    try {
        const url = API_URLS.videoCallQaLessonReport.replace('{lessonId}', encodeURIComponent(lessonId));
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching video call QA report:', error);
        throw error;
    }
};
